import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import api from "@/lib/api";
import { CET_RANK_DATA } from "../../data/cet_rank_data";

const PREF_TOKEN_KEY = "pref_token";
const PREF_USER_KEY = "pref_user";
function getAuthHeader() { return { Authorization: `Bearer ${localStorage.getItem(PREF_TOKEN_KEY)}` }; }

const SearchableMultiSelect = ({ label, options, selected, setSelected, enablePriority, setPriorityVal }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = (options || []).filter(o => o.toLowerCase().includes(search.toLowerCase()));

  const toggleOption = (val) => {
    const exists = selected.find(i => i.value === val);
    if (exists) setSelected(selected.filter(i => i.value !== val));
    else setSelected([...selected, { value: val, priority: null }]);
    setSearch("");
  };

  const removeOption = (e, val) => {
    e.stopPropagation();
    setSelected(selected.filter(i => i.value !== val));
  };

  return (
    <div className="pcw-sms-wrapper" ref={wrapperRef}>
      <label>{label}</label>
      <div className={`pcw-sms-box ${isOpen ? 'open' : ''}`} onClick={() => setIsOpen(true)}>
        <div className="pcw-sms-tags">
          {selected.map(s => (
            <span key={s.value} className="pcw-sms-tag">
              {s.value}
              {enablePriority && (
                <input 
                  className="pcw-sms-priority" 
                  value={s.priority || ""} 
                  onChange={e => setPriorityVal(selected, setSelected, s.value, e.target.value)} 
                  placeholder="#" 
                  onClick={e => e.stopPropagation()}
                />
              )}
              <i className="fas fa-times" onClick={(e) => removeOption(e, s.value)}></i>
            </span>
          ))}
          <input 
            className="pcw-sms-input" 
            placeholder={selected.length === 0 ? "Select options..." : ""} 
            value={search} 
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <i className="fas fa-chevron-down pcw-sms-arrow"></i>
        {isOpen && (
          <div className="pcw-sms-menu">
            {filteredOptions.length > 0 ? filteredOptions.map(o => {
              const isSelected = selected.find(i => i.value === o);
              return (
                <div key={o} className={`pcw-sms-option ${isSelected ? 'selected' : ''}`} onClick={() => toggleOption(o)}>
                  <div className="pcw-sms-checkbox">{isSelected && <i className="fas fa-check"></i>}</div>
                  {o}
                </div>
              );
            }) : <div className="pcw-sms-nores">No results found</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default function PrefCounselorWorkspace() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [examTab, setExamTab] = useState("CET");
  const [showFilters, setShowFilters] = useState(true);
  const [studentVersions, setStudentVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [showSaveVersionModal, setShowSaveVersionModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateVersionName, setDuplicateVersionName] = useState("");
  const [newVersionName, setNewVersionName] = useState("");
  const [includeOpenCategory, setIncludeOpenCategory] = useState(true);

  // Search & Add manual states
  const [manualQuery, setManualQuery] = useState("");
  const [manualResults, setManualResults] = useState([]);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [selectedCollegeToAdd, setSelectedCollegeToAdd] = useState(null);
  const [insertPosition, setInsertPosition] = useState("");
  const [showPositionModal, setShowPositionModal] = useState(false);

  // Shared filters (Task 1: common across tabs)
  const [filterOptions, setFilterOptions] = useState({ cities: [], course_groups: [], categories: [] });
  const [percentile, setPercentile] = useState("");
  const [rank, setRank] = useState("");
  const [percPlusBuf, setPercPlusBuf] = useState("0");    // Dream: above student percentile
  const [percMinusBuf, setPercMinusBuf] = useState("5");   // Safe: below student percentile
  const [rankPlusBuf, setRankPlusBuf] = useState("5000");  // Safe: worse ranks to include
  const [rankMinusBuf, setRankMinusBuf] = useState("0");   // Dream: better ranks to include
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedCourseGroups, setSelectedCourseGroups] = useState([]);
  const [baseCategory, setBaseCategory] = useState("OPEN");
  const [specialQuotas, setSpecialQuotas] = useState([]);
  const [enablePriority, setEnablePriority] = useState(false);
  const [printRank, setPrintRank] = useState(true);
  const [printBranchRank, setPrintBranchRank] = useState(false);
  // CET-only: Home University & Gender filters
  const [homeUniversity, setHomeUniversity] = useState("");
  const [gender, setGender] = useState("General");
  const [opt1BufferPercent, setOpt1BufferPercent] = useState("15");
  const [opt2BufferPercent, setOpt2BufferPercent] = useState("5");
  const [showRankPredictor, setShowRankPredictor] = useState(true);
  const [is2026R1, setIs2026R1] = useState(false);
  
  // Cutoff Checker States
  const [showCutoffPage, setShowCutoffPage] = useState(false);
  const [cutoffColleges, setCutoffColleges] = useState([]);
  const [selectedCutoffColleges, setSelectedCutoffColleges] = useState([]);
  const [cutoffBranches, setCutoffBranches] = useState([]);
  const [selectedCutoffBranches, setSelectedCutoffBranches] = useState([]);
  const [cutoffCategory, setCutoffCategory] = useState("OPEN");
  const [cutoffSpecialQuotas, setCutoffSpecialQuotas] = useState([]);
  const [cutoffIncludeOpen, setCutoffIncludeOpen] = useState(true);
  const [cutoffHomeUniversity, setCutoffHomeUniversity] = useState("");
  const [cutoffGender, setCutoffGender] = useState("General");
  const [cutoffResults, setCutoffResults] = useState([]);
  const [loadingCutoffColleges, setLoadingCutoffColleges] = useState(false);
  const [loadingCutoffBranches, setLoadingCutoffBranches] = useState(false);
  const [searchingCutoffs, setSearchingCutoffs] = useState(false);
  const [collegeSearchQuery, setCollegeSearchQuery] = useState("");
  const [branchSearchQuery, setBranchSearchQuery] = useState("");
  const [showCollegeDropdown, setShowCollegeDropdown] = useState(false);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const collegeDropdownRef = useRef(null);
  const branchDropdownRef = useRef(null);


  const handleRankChange = (val) => {
    setRank(val);
    if (val !== undefined && val !== null && val !== "") {
      setRankMinusBuf(val.toString());
      setRankPlusBuf(val.toString());
    }
  };

  // Custom Admission Zone Settings state
  const [zoneBasis, setZoneBasis] = useState("rank");
  const [zoneRankUpper, setZoneRankUpper] = useState("0");
  const [zoneRankLower, setZoneRankLower] = useState("3000");
  const [zonePercUpper, setZonePercUpper] = useState("0.0");
  const [zonePercLowerMode, setZonePercLowerMode] = useState("auto");
  const [zonePercLowerCustom, setZonePercLowerCustom] = useState("2.5");

  // Results — unified list that IS the preference list (Task 4)
  const [results, setResults] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [removedIds, setRemovedIds] = useState(new Set());
  const [resultSearch, setResultSearch] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [saving, setSaving] = useState(false);

  // Click-to-Move reordering state
  const [selectedMoveIdx, setSelectedMoveIdx] = useState(null);

  // Scroll-to-top state and ref
  const mainRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Persistence logic (Task: Save working state)
  const isStateLoading = useRef(false);

  const fetchStudentVersions = async (studentId) => {
    try {
      const res = await api.get(`/preferences/drafts/${studentId}`, { headers: getAuthHeader() });
      setStudentVersions(res.data || []);
    } catch (err) {
      console.error("Failed to fetch student versions", err);
    }
  };

  const handleSelectVersion = (v) => {
    setActiveVersionId(v.id);
    setExamTab(v.list_type);
    
    isStateLoading.current = true;
    const filters = v.filters_used || {};
    setPercentile(filters.percentile ? filters.percentile.toString() : "");
    setRank(filters.rank ? filters.rank.toString() : "");
    setPercPlusBuf(filters.percentile_plus_buffer !== undefined ? filters.percentile_plus_buffer.toString() : "0");
    setPercMinusBuf(filters.percentile_minus_buffer !== undefined ? filters.percentile_minus_buffer.toString() : "5");
    setRankPlusBuf(filters.rank_plus_buffer !== undefined ? filters.rank_plus_buffer.toString() : "5000");
    setRankMinusBuf(filters.rank_minus_buffer !== undefined ? filters.rank_minus_buffer.toString() : "0");
    setSelectedCities(filters.cities || []);
    setSelectedCourseGroups(filters.course_groups || []);
    setBaseCategory(filters.base_category || "OPEN");
    setSpecialQuotas(filters.special_quotas || []);
    setEnablePriority(filters.enable_priority || false);
    setPrintRank(filters.print_rank !== undefined ? filters.print_rank : true);
    setPrintBranchRank(filters.print_branch_rank !== undefined ? filters.print_branch_rank : false);
    setHomeUniversity(filters.home_university || "");
    setGender(filters.gender || "General");
    
    if (filters.zone_basis) setZoneBasis(filters.zone_basis);
    if (filters.zone_rank_upper !== undefined) setZoneRankUpper(filters.zone_rank_upper.toString());
    if (filters.zone_rank_lower !== undefined) setZoneRankLower(filters.zone_rank_lower.toString());
    if (filters.zone_perc_upper !== undefined) setZonePercUpper(filters.zone_perc_upper.toString());
    if (filters.zone_perc_lower_mode) setZonePercLowerMode(filters.zone_perc_lower_mode);
    if (filters.zone_perc_lower_custom !== undefined) setZonePercLowerCustom(filters.zone_perc_lower_custom.toString());

    const prefs = v.preferences || [];
    const prefsWithKeys = prefs.map((p, idx) => ({
      ...p,
      _key: p._key || `${p["INSTITUTE"] || p["Institute Name"] || "inst"}-${p["BRANCH NAME"] || p["Course Name"] || "branch"}-${idx}-${Date.now()}`
    }));
    setResults(prefsWithKeys);
    setRemovedIds(new Set());
    setShowFilters(prefsWithKeys.length === 0);
    isStateLoading.current = false;
  };

  const clearState = () => {
    setPercentile("");
    setRank("");
    setPercPlusBuf("0");
    setPercMinusBuf("5");
    setRankPlusBuf("5000");
    setRankMinusBuf("0");
    setSelectedCities([]);
    setSelectedCourseGroups([]);
    setBaseCategory("OPEN");
    setSpecialQuotas([]);
    setEnablePriority(false);
    setPrintRank(true);
    setPrintBranchRank(false);
    setHomeUniversity("");
    setGender("General");
    setResults([]);
    setRemovedIds(new Set());
    setStudentVersions([]);
    setActiveVersionId(null);
  };

  const loadStudentState = async (studentId, listType) => {
    isStateLoading.current = true;
    
    // Synchronously wipe state to ensure no leakage from the previous student while loading
    setPercentile("");
    setRank("");
    setPercPlusBuf("0");
    setPercMinusBuf("5");
    setRankPlusBuf("5000");
    setRankMinusBuf("0");
    setBaseCategory("OPEN");
    setSpecialQuotas([]);
    setSelectedCities([]);
    setSelectedCourseGroups([]);
    setEnablePriority(false);
    setPrintRank(true);
    setPrintBranchRank(false);
    setHomeUniversity("");
    setGender("General");
    setResults([]);
    setRemovedIds(new Set());
    setShowFilters(true);
    setManualQuery("");
    setManualResults([]);
    setOpt1BufferPercent("15");
    setOpt2BufferPercent("5");
    setResultSearch("");
    setSortField("");
    setSortDir("asc");
    setSelectedMoveIdx(null);

    fetchStudentVersions(studentId);
    
    // 1. Load shared filters from localStorage
    const sharedKey = `pref_shared_state_${studentId}`;
    const sharedStateStr = localStorage.getItem(sharedKey);
    let sharedCities = [];
    let sharedCourseGroups = [];
    if (sharedStateStr) {
      try {
        const sharedState = JSON.parse(sharedStateStr);
        sharedCities = sharedState.selectedCities || [];
        sharedCourseGroups = sharedState.selectedCourseGroups || [];
        setSelectedCities(sharedCities);
        setSelectedCourseGroups(sharedCourseGroups);
      } catch (e) {
        console.error("Failed to parse shared state", e);
      }
    } else {
      setSelectedCities([]);
      setSelectedCourseGroups([]);
    }

    // 2. Load tab-specific state from localStorage
    const stateKey = `pref_work_state_${studentId}_${listType}`;
    const localStateStr = localStorage.getItem(stateKey);
    
    if (localStateStr) {
      try {
        const state = JSON.parse(localStateStr);
        setPercentile(state.percentile || "");
        setRank(state.rank || "");
        setPercPlusBuf(state.percPlusBuf !== undefined ? state.percPlusBuf : "0");
        setPercMinusBuf(state.percMinusBuf !== undefined ? state.percMinusBuf : "5");
        setRankPlusBuf(state.rankPlusBuf !== undefined ? state.rankPlusBuf : "5000");
        setRankMinusBuf(state.rankMinusBuf !== undefined ? state.rankMinusBuf : "0");
        setBaseCategory(state.baseCategory || "OPEN");
        setSpecialQuotas(state.specialQuotas || []);
        setEnablePriority(state.enablePriority || false);
        setPrintRank(state.printRank !== undefined ? state.printRank : true);
        setPrintBranchRank(state.printBranchRank !== undefined ? state.printBranchRank : false);
        setHomeUniversity(state.homeUniversity || "");
        setGender(state.gender || "General");
        setResults(state.results || []);
        setRemovedIds(new Set(state.removedIds || []));
        setShowFilters((state.results || []).length === 0);
        isStateLoading.current = false;
        return;
      } catch (e) {
        console.error("Failed to parse local state", e);
      }
    }

    // Fallback: load from database draft
    try {
      const res = await api.get(`/preferences/drafts/${studentId}/${listType}`, { headers: getAuthHeader() });
      if (res.data) {
        const draft = res.data;
        const filters = draft.filters_used || {};
        setPercentile(filters.percentile ? filters.percentile.toString() : "");
        setRank(filters.rank ? filters.rank.toString() : "");
        setPercPlusBuf(filters.percentile_plus_buffer !== undefined ? filters.percentile_plus_buffer.toString() : "0");
        setPercMinusBuf(filters.percentile_minus_buffer !== undefined ? filters.percentile_minus_buffer.toString() : "5");
        setRankPlusBuf(filters.rank_plus_buffer !== undefined ? filters.rank_plus_buffer.toString() : "5000");
        setRankMinusBuf(filters.rank_minus_buffer !== undefined ? filters.rank_minus_buffer.toString() : "0");
        setBaseCategory(filters.base_category || "OPEN");
        setSpecialQuotas(filters.special_quotas || []);
        setEnablePriority(filters.enable_priority || false);
        setPrintRank(filters.print_rank !== undefined ? filters.print_rank : true);
        setPrintBranchRank(filters.print_branch_rank !== undefined ? filters.print_branch_rank : false);
        setHomeUniversity(filters.home_university || "");
        setGender(filters.gender || "General");
        
        // Only set shared if they weren't loaded from localStorage
        if (sharedCities.length === 0) setSelectedCities(filters.cities || []);
        if (sharedCourseGroups.length === 0) setSelectedCourseGroups(filters.course_groups || []);
        
        const prefs = draft.preferences || [];
        const prefsWithKeys = prefs.map((p, idx) => ({
          ...p,
          _key: p._key || `${p["INSTITUTE"] || p["Institute Name"] || "inst"}-${p["BRANCH NAME"] || p["Course Name"] || "branch"}-${idx}-${Date.now()}`
        }));
        setResults(prefsWithKeys);
        setRemovedIds(new Set());
        setShowFilters(prefsWithKeys.length === 0);
      } else {
        setPercentile("");
        setRank("");
        setPercPlusBuf("0");
        setPercMinusBuf("5");
        setRankPlusBuf("5000");
        setRankMinusBuf("0");
        setSelectedCities([]);
        setSelectedCourseGroups([]);
        setEnablePriority(false);
        setPrintRank(true);
        setPrintBranchRank(false);
        setHomeUniversity("");
        setGender("General");
        setResults([]);
        setRemovedIds(new Set());
        setShowFilters(true);
      }
    } catch {
      setPercentile("");
      setRank("");
      setPercPlusBuf("0");
      setPercMinusBuf("5");
      setRankPlusBuf("5000");
      setRankMinusBuf("0");
      setSelectedCities([]);
      setSelectedCourseGroups([]);
      setEnablePriority(false);
      setPrintRank(true);
      setPrintBranchRank(false);
      setHomeUniversity("");
      setGender("General");
      setResults([]);
      setRemovedIds(new Set());
      setShowFilters(true);
    }
    isStateLoading.current = false;
  };

  const handleManualSearchChange = async (e) => {
    const val = e.target.value;
    setManualQuery(val);
    if (val.trim().length < 3) {
      setManualResults([]);
      return;
    }
    try {
      let url = `/preferences/search-colleges?exam_type=${examTab}&query=${val}`;
      if (examTab === "CET") {
        if (homeUniversity) url += `&home_university=${encodeURIComponent(homeUniversity)}`;
        if (gender) url += `&gender=${encodeURIComponent(gender)}`;
      }
      const res = await api.get(url, { headers: getAuthHeader() });
      const existingKeys = new Set(results.map(r => {
        const code = r["Branch Code"] || r["Choice Code"];
        const cat = r["CATEGORY"] || "";
        const inst = r["INSTITUTE"] || r["Institute Name"] || "";
        const branch = r["BRANCH NAME"] || r["Course Name"] || "";
        return code ? `${code}_${cat}` : `${inst}_${branch}_${cat}`;
      }));
      const availableResults = (res.data || []).filter(item => {
        const code = item["Branch Code"] || item["Choice Code"];
        const cat = item["CATEGORY"] || "";
        const inst = item["INSTITUTE"] || item["Institute Name"] || "";
        const branch = item["BRANCH NAME"] || item["Course Name"] || "";
        const key = code ? `${code}_${cat}` : `${inst}_${branch}_${cat}`;
        return !existingKeys.has(key);
      });
      setManualResults(availableResults);
    } catch {
      setManualResults([]);
    }
  };

  const handleSelectCollegeToAdd = (college) => {
    const currentDisplay = getDisplayResults();
    setSelectedCollegeToAdd(college);
    setInsertPosition((currentDisplay.length + 1).toString());
    setShowPositionModal(true);
  };

  const handleInsertCollege = () => {
    if (!selectedCollegeToAdd) return;
    
    if (isActiveVersionFinalized()) {
      toast.error("Active version is finalized and locked.");
      return;
    }

    const currentDisplay = getDisplayResults();
    const pos = parseInt(insertPosition, 10);
    if (isNaN(pos) || pos < 1 || pos > currentDisplay.length + 1) {
      toast.error(`Please enter a position between 1 and ${currentDisplay.length + 1}`);
      return;
    }
    
    const codeKey = examTab === "CET" ? "Branch Code" : "Choice Code";
    const instKey = examTab === "CET" ? "INSTITUTE" : "Institute Name";
    const branchKey = examTab === "CET" ? "BRANCH NAME" : "Course Name";
    
    const code = selectedCollegeToAdd[codeKey];
    const isDuplicate = results.some(item => {
      if (code && item[codeKey] === code) return true;
      return item[instKey] === selectedCollegeToAdd[instKey] && item[branchKey] === selectedCollegeToAdd[branchKey];
    });
    
    if (isDuplicate) {
      if (!window.confirm("This college option is already in your preference list. Do you still want to add it?")) {
        return;
      }
    }
    
    const newKey = `${selectedCollegeToAdd[instKey] || "inst"}-${selectedCollegeToAdd[branchKey] || "branch"}-${Date.now()}`;
    const newItem = {
      ...selectedCollegeToAdd,
      _key: newKey,
      preference_number: pos
    };
    
    const newResults = [...results];
    let insertIndex = newResults.length;

    if (pos <= currentDisplay.length) {
      const targetItem = currentDisplay[pos - 1];
      const foundIdx = newResults.findIndex(r => r._key ? r._key === targetItem._key : JSON.stringify(r) === JSON.stringify(targetItem));
      if (foundIdx !== -1) {
        insertIndex = foundIdx;
      }
    }

    newResults.splice(insertIndex, 0, newItem);
    
    const reindexedResults = newResults.map((item, index) => ({
      ...item,
      preference_number: index + 1
    }));
    
    setSortField(""); // Turn off auto-column sorting so manual order is preserved!
    setResults(reindexedResults);
    
    setManualQuery("");
    setManualResults([]);
    setShowManualAdd(false);
    setSelectedCollegeToAdd(null);
    setShowPositionModal(false);
    
    toast.success(`Inserted at position #${pos}`);
  };

  // Auto-save shared filters to localStorage
  useEffect(() => {
    if (!selectedStudent || isStateLoading.current || activeVersionId !== null) return;
    const sharedKey = `pref_shared_state_${selectedStudent.id}`;
    const sharedState = {
      selectedCities,
      selectedCourseGroups
    };
    localStorage.setItem(sharedKey, JSON.stringify(sharedState));
  }, [selectedCities, selectedCourseGroups, selectedStudent, activeVersionId, includeOpenCategory]);

  // Auto-save tab-specific state to localStorage
  useEffect(() => {
    if (!selectedStudent || isStateLoading.current || activeVersionId !== null) return;
    const stateKey = `pref_work_state_${selectedStudent.id}_${examTab}`;
    const state = {
      percentile,
      rank,
      percPlusBuf,
      percMinusBuf,
      rankPlusBuf,
      rankMinusBuf,
      printRank,
      printBranchRank,
      baseCategory,
      specialQuotas,
      enablePriority,
      homeUniversity,
      gender,
      results,
      removedIds: Array.from(removedIds)
    };
    localStorage.setItem(stateKey, JSON.stringify(state));
  }, [percentile, rank, percPlusBuf, percMinusBuf, rankPlusBuf, rankMinusBuf, printRank, printBranchRank, baseCategory, specialQuotas, enablePriority, homeUniversity, gender, results, removedIds, selectedStudent, examTab, activeVersionId, includeOpenCategory]);

  // Student modal
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [newStudentForm, setNewStudentForm] = useState({ registration_number: "", name: "", mobile: "", password: "", category: "OPEN", special_reservation: "", branch: "AME Pune" });
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [editStudentForm, setEditStudentForm] = useState({ id: "", registration_number: "", name: "", mobile: "", password: "", category: "OPEN", special_reservation: "", branch: "AME Pune" });

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem(PREF_USER_KEY) || "null");
    if (!u || (u.role !== "COUNSELOR" && u.role !== "ADMIN")) { navigate("/engineering-admission/portal-login"); return; }
    setUser(u);
  }, [navigate]);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await api.get(`/preferences/students?search=${studentSearch}`, { headers: getAuthHeader() });
      setStudents(res.data);
    } catch { /* ignore */ }
  }, [studentSearch]);

  useEffect(() => { if (user) fetchStudents(); }, [user, fetchStudents]);

  // Task 1: Load COMMON filters dynamically based on is2026R1 toggle
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/preferences/filters/common?is_2026_r1=${is2026R1}`);
        setFilterOptions(res.data);
      } catch { /* ignore */ }
    })();
  }, [is2026R1]);

  // Fetch all 2026 R1 colleges when checker page is shown
  useEffect(() => {
    if (showCutoffPage && cutoffColleges.length === 0) {
      (async () => {
        setLoadingCutoffColleges(true);
        try {
          const res = await api.get("/preferences/2026-r1/colleges");
          setCutoffColleges(res.data.colleges || []);
        } catch (e) {
          toast.error("Failed to load college list.");
        } finally {
          setLoadingCutoffColleges(false);
        }
      })();
    }
  }, [showCutoffPage, cutoffColleges]);

  // Fetch unique branches for selected colleges
  useEffect(() => {
    if (!showCutoffPage) return;
    (async () => {
      setLoadingCutoffBranches(true);
      try {
        const res = await api.post("/preferences/2026-r1/branches", {
          colleges: selectedCutoffColleges.map(c => c.code)
        });
        setCutoffBranches(res.data.branches || []);
        setSelectedCutoffBranches(prev => prev.filter(b => (res.data.branches || []).includes(b)));
      } catch (e) {
        toast.error("Failed to load branches.");
      } finally {
        setLoadingCutoffBranches(false);
      }
    })();
  }, [selectedCutoffColleges, showCutoffPage]);

  // Handle click outside for cutoff explorer dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (collegeDropdownRef.current && !collegeDropdownRef.current.contains(event.target)) {
        setShowCollegeDropdown(false);
      }
      if (branchDropdownRef.current && !branchDropdownRef.current.contains(event.target)) {
        setShowBranchDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearchCutoffs = async () => {
    setSearchingCutoffs(true);
    try {
      const res = await api.post("/preferences/2026-r1/query", {
        colleges: selectedCutoffColleges.map(c => c.code),
        branches: selectedCutoffBranches,
        base_category: cutoffCategory,
        special_quotas: cutoffSpecialQuotas,
        include_open_category: cutoffIncludeOpen,
        home_university: cutoffHomeUniversity || null,
        gender: cutoffGender
      });
      setCutoffResults(res.data.results || []);
      if ((res.data.results || []).length === 0) {
        toast.info("No matching records found.");
      } else {
        toast.success(`Found ${(res.data.results || []).length} records!`);
      }
    } catch (e) {
      toast.error("Failed to fetch cutoff data.");
    } finally {
      setSearchingCutoffs(false);
    }
  };

  // Scroll-to-top: track scroll position on .pcw-main
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const onScroll = () => setShowScrollTop(el.scrollTop > 300);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [user]);

  // Keyboard shortcut: Alt+T or Alt+ArrowUp to scroll to top
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.altKey && (e.key === 't' || e.key === 'T' || e.key === 'ArrowUp')) {
        e.preventDefault();
        mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Toggle multi-select helpers
  const toggleFilter = (list, setList, value) => {
    const exists = list.find(i => i.value === value);
    if (exists) setList(list.filter(i => i.value !== value));
    else setList([...list, { value, priority: null }]);
  };
  const setPriorityVal = (list, setList, value, priority) => {
    setList(list.map(i => i.value === value ? { ...i, priority: priority ? parseInt(priority) : null } : i));
  };

  const handleGenerate = async () => {
    if (isActiveVersionFinalized()) {
      toast.error("This version is finalized. Save as a new version to modify.");
      return;
    }
    if (!percentile && !rank) { 
      toast.error("Please enter either a Percentile or a Rank/Merit"); 
      return; 
    }
    setGenerating(true);
    setRemovedIds(new Set());
    try {
      const endpoint = examTab === "CET" ? "/preferences/generate-cet" : "/preferences/generate-jee";
      // Normalize cities and course_groups to ensure {value, priority} format
      const normalizeCities = (selectedCities || []).map(c => typeof c === 'string' ? { value: c, priority: null } : c);
      const normalizeCourseGroups = (selectedCourseGroups || []).map(c => typeof c === 'string' ? { value: c, priority: null } : c);
      const body = { 
        percentile: percentile ? parseFloat(percentile) : null,
        percentile_plus_buffer: percPlusBuf ? parseFloat(percPlusBuf) : 0.0,
        percentile_minus_buffer: percMinusBuf ? parseFloat(percMinusBuf) : 5.0,
        rank: rank ? parseFloat(rank) : null,
        rank_plus_buffer: rankPlusBuf ? parseFloat(rankPlusBuf) : 5000.0,
        rank_minus_buffer: rankMinusBuf ? parseFloat(rankMinusBuf) : 0.0,
        cities: normalizeCities, 
        course_groups: normalizeCourseGroups, 
        enable_priority: enablePriority 
      };
      if (examTab === "CET") {
        body.base_category = baseCategory;
        body.special_quotas = specialQuotas;
        if (homeUniversity) body.home_university = homeUniversity;
        body.gender = gender || "General";
        body.include_open_category = includeOpenCategory;
        body.is_2026_r1 = is2026R1;
      }
      const res = await api.post(endpoint, body);
      const dataWithKeys = (res.data.data || []).map((item, idx) => ({
        ...item,
        _key: `${item["INSTITUTE"] || item["Institute Name"] || "inst"}-${item["BRANCH NAME"] || item["Course Name"] || "branch"}-${idx}-${Date.now()}`
      }));
      setResults(dataWithKeys);
      setShowFilters(false);
      toast.success(`Found ${res.data.count} colleges!`);
    } catch (err) {
      console.error("Generate error:", err?.response?.data || err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to generate. Please check filters.");
      setShowFilters(true);
    }
    finally { setGenerating(false); }
  };

  const removeFromResults = (item) => {
    if (isActiveVersionFinalized()) {
      toast.info("This version is finalized. Save as a new version to make changes.");
      return;
    }
    setResults(prev => prev.filter(r => r._key ? r._key !== item._key : JSON.stringify(r) !== JSON.stringify(item)));
  };

  const getPredictedRanks = () => {
    if (!percentile) return null;
    const pVal = parseFloat(percentile);
    if (isNaN(pVal) || pVal < 0 || pVal > 100) return null;

    // Option 1: find nearest percentile match in CET_RANK_DATA
    let nearestMatch = null;
    let minDiff = Infinity;
    for (const [perc, sml] of CET_RANK_DATA) {
      const diff = Math.abs(perc - pVal);
      if (diff < minDiff) {
        minDiff = diff;
        nearestMatch = { perc, sml };
      }
    }

    let predicted1 = null;
    if (nearestMatch) {
      const x = nearestMatch.sml;
      const bufPct = parseFloat(opt1BufferPercent) || 0;
      predicted1 = Math.round(x + (x * (bufPct / 100)));
    }

    // Option 2: (100 - percentile) * 454069 / 100
    const predicted2Base = Math.round(((100 - pVal) * 454069) / 100);
    const bufPct2 = parseFloat(opt2BufferPercent) || 0;
    const predicted2 = Math.round(predicted2Base - (predicted2Base * (bufPct2 / 100)));

    // Option 3: Equivalent Percentile Match
    const equivPerc = 100 - (100 - pVal) * 4.9 / 4.2;
    let nearestMatch3 = null;
    let minDiff3 = Infinity;
    for (const [perc, sml] of CET_RANK_DATA) {
      const diff = Math.abs(perc - equivPerc);
      if (diff < minDiff3) {
        minDiff3 = diff;
        nearestMatch3 = { perc, sml };
      }
    }
    const predicted3 = nearestMatch3 ? nearestMatch3.sml : null;

    return {
      nearest1: nearestMatch,
      predicted1,
      predicted2,
      equivPerc,
      nearest3: nearestMatch3,
      predicted3
    };
  };

  // City to University mapping for client-side dynamic filtering (mirrors backend)
  const CITY_TO_UNIVERSITY_CLIENT = {
    'AHILYANAGAR': 'SPPU', 'AKOLA': 'SGBAU', 'AMRAVATI': 'SGBAU', 'BARAMATI': 'SPPU',
    'BEED': 'BAMU', 'BHANDARA': 'RTMNU', 'BULDHANA': 'SGBAU', 'CHANDRAPUR': 'GUG',
    'CHANDWAD': 'SPPU', 'DHARASHIV': 'SRTMUN', 'DHULE': 'NMU', 'ICHALKARANJI': 'SUK',
    'JALGAON': 'NMU', 'JALNA': 'BAMU', 'KOLHAPUR': 'SUK', 'KOPERGAON': 'SPPU',
    'LATUR': 'SRTMUN', 'LONAVALA': 'SPPU', 'MUMBAI': 'MU', 'MUNMBAI': 'MU',
    'NAGPUR': 'RTMNU', 'NANDED': 'SRTMUN', 'NANDURBAR': 'NMU', 'NASHIK': 'SPPU',
    'NAVI MUMBAI': 'MU', 'PALGHAR': 'MU', 'PANDHARPUR': 'PAHSU', 'PARBHANI': 'SRTMUN',
    'PUNE': 'SPPU', 'PUNE RURAL': 'SPPU', 'RAIGAD': 'MU', 'RATNAGIRI': 'MU',
    'SAMBHAJI NAGAR': 'BAMU', 'SAMBHAJINAGAR': 'BAMU', 'SANGAMNER': 'SPPU',
    'SANGLI': 'SUK', 'SATARA': 'SUK', 'SINDHUDURG': 'MU', 'SOLAPUR': 'PAHSU',
    'THANE': 'MU', 'WARDHA': 'RTMNU', 'WASHIM': 'SGBAU', 'YAVATMAL': 'SGBAU'
  };
  const getUniversityForCity = (city) => {
    if (!city) return 'OTHER';
    return CITY_TO_UNIVERSITY_CLIENT[city.trim().toUpperCase()] || 'OTHER';
  };

  // Get final active items (not removed), with optional CET client-side university/gender filter
  const getActiveItems = () => {
    return results.filter(r => {
      if (removedIds.has(JSON.stringify(r))) return false;
      // Only apply CET-specific filters when we are in CET context
      if (examTab === 'CET') {
        const category = r['CATEGORY'];
        // Gender filter
        if (gender === 'General' && category && category.startsWith('L')) return false;
        // Home University filter
        if (homeUniversity) {
          const city = r['CITY'];
          const recUni = getUniversityForCity(city);
          if (recUni === homeUniversity) {
            if (category && category.endsWith('O')) return false;
          } else {
            if (category && category.endsWith('H')) return false;
          }
        }
      }
      return true;
    });
  };

  // Two-Click Reordering logic
  const handleRowClick = (index) => {
    if (isActiveVersionFinalized()) {
      toast.info("This version is finalized. Save as a new version to make changes.");
      return;
    }
    if (selectedMoveIdx === null) {
      setSelectedMoveIdx(index);
    } else {
      if (selectedMoveIdx === index) {
        setSelectedMoveIdx(null);
      } else {
        moveItem(selectedMoveIdx, index);
        setSelectedMoveIdx(null);
      }
    }
  };

  const moveItem = (sourceIdx, targetIdx) => {
    const displayList = getDisplayResults();
    const itemA = displayList[sourceIdx];

    // Commit sort to the main array if an active sort exists
    let baseResults = results;
    if (sortField) {
      baseResults = [...results].sort((a, b) => {
        let av = parseFloat(a[sortField]) || 0, bv = parseFloat(b[sortField]) || 0;
        return sortDir === "asc" ? av - bv : bv - av;
      });
      setSortField(""); // Clear visual sort since it's now permanent
    }

    const masterSourceIdx = baseResults.findIndex(r => JSON.stringify(r) === JSON.stringify(itemA));

    if (masterSourceIdx > -1) {
      const newResults = baseResults.filter(r => JSON.stringify(r) !== JSON.stringify(itemA));
      const targetItem = displayList[targetIdx];
      let insertIdx = newResults.findIndex(r => JSON.stringify(r) === JSON.stringify(targetItem));

      if (insertIdx === -1) {
        insertIdx = newResults.length;
      }

      newResults.splice(insertIdx, 0, itemA);
      setResults(newResults);
      setSortField(""); // Ensure it's cleared if it wasn't above
      toast.success(`Moved option successfully to position #${targetIdx + 1}`);
    }
  };

  // Sort + filter display
  const getDisplayResults = () => {
    let filtered = results.filter(r => !removedIds.has(JSON.stringify(r)));
    if (resultSearch.trim()) {
      const s = resultSearch.toLowerCase();
      filtered = filtered.filter(r => {
        const inst = (r["INSTITUTE"] || r["Institute Name"] || "").toLowerCase();
        const branch = (r["BRANCH NAME"] || r["Course Name"] || "").toLowerCase();
        return inst.includes(s) || branch.includes(s);
      });
    }
    if (sortField) {
      filtered.sort((a, b) => {
        let av = parseFloat(a[sortField]) || 0, bv = parseFloat(b[sortField]) || 0;
        return sortDir === "asc" ? av - bv : bv - av;
      });
    }
    return filtered;
  };

  const isActiveVersionFinalized = () => {
    if (!activeVersionId) return false;
    const v = studentVersions.find(ver => ver.id === activeVersionId);
    return v?.status === "FINALIZED";
  };

  const getAdmissionCategory = (item) => {
    if (item.manualZone) {
      if (item.manualZone === "Dream") return { label: "Dream", colorClass: "dream", borderLeft: "3px solid #c084fc" };
      if (item.manualZone === "Safe") return { label: "Safe", colorClass: "safe", borderLeft: "3px solid #34d399" };
      return { label: "Target", colorClass: "target", borderLeft: "3px solid #fbbf24" };
    }
    const studentRank = parseFloat(rank);
    const itemRank = parseFloat(item.Rank || item["AI Merit"]);
    const studentPerc = parseFloat(percentile);
    const itemPerc = parseFloat(item.Percentile || item["Percen Tile"]);

    // Determine basis
    const useRank = zoneBasis === 'rank';

    if (useRank && itemRank) {
      const upperLimit = parseFloat(zoneRankUpper) || 0;
      const lowerLimit = parseFloat(zoneRankLower) || 999999;

      if (itemRank < upperLimit) return { label: "Dream", colorClass: "dream", borderLeft: "3px solid #c084fc" };
      else if (itemRank <= lowerLimit) return { label: "Target", colorClass: "target", borderLeft: "3px solid #fbbf24" };
      else return { label: "Safe", colorClass: "safe", borderLeft: "3px solid #34d399" };
    } else if (itemPerc) {
      let targetLowerBuf = 5.0;
      if (studentPerc) {
        if (studentPerc >= 98) targetLowerBuf = 1.0;
        else if (studentPerc >= 90) targetLowerBuf = 2.5;
        else if (studentPerc >= 80) targetLowerBuf = 4.0;
        else if (studentPerc >= 70) targetLowerBuf = 6.0;
        else targetLowerBuf = 10.0;
      }

      const upperLimit = parseFloat(zonePercUpper) || 100.0;
      const lowerLimit = zonePercLowerMode === 'custom'
        ? (parseFloat(zonePercLowerCustom) || 0.0)
        : (studentPerc ? studentPerc - targetLowerBuf : 0.0);

      if (itemPerc > upperLimit) return { label: "Dream", colorClass: "dream", borderLeft: "3px solid #c084fc" };
      else if (itemPerc >= lowerLimit) return { label: "Target", colorClass: "target", borderLeft: "3px solid #fbbf24" };
      else return { label: "Safe", colorClass: "safe", borderLeft: "3px solid #34d399" };
    }
    return { label: "Target", colorClass: "target", borderLeft: "3px solid #fbbf24" };
  };

  const handleToggleZone = (e, displayIdx) => {
    e.stopPropagation();
    if (isActiveVersionFinalized()) {
      toast.info("This version is finalized. Save as a new version to make changes.");
      return;
    }
    const currentItem = displayResults[displayIdx];
    const mainIdx = results.findIndex(r => r._key === currentItem._key);
    if (mainIdx !== -1) {
      const newResults = [...results];
      const currentZone = getAdmissionCategory(newResults[mainIdx]).label;
      let nextZone = "Target";
      if (currentZone === "Target") nextZone = "Safe";
      else if (currentZone === "Safe") nextZone = "Dream";
      else nextZone = "Target";
      
      newResults[mainIdx] = { ...newResults[mainIdx], manualZone: nextZone };
      setResults(newResults);
    }
  };

  const moveItemInDisplay = (displayIdx, direction, e) => {
    e.stopPropagation();
    if (isActiveVersionFinalized()) {
      toast.info("This version is finalized. Save as a new version to make changes.");
      return;
    }
    const currentItem = displayResults[displayIdx];
    const targetIdx = direction === "up" ? displayIdx - 1 : displayIdx + 1;
    if (targetIdx < 0 || targetIdx >= displayResults.length) return;
    const targetItem = displayResults[targetIdx];

    // Commit sort to the main array if an active sort exists
    let baseResults = [...results];
    if (sortField) {
      baseResults.sort((a, b) => {
        let av = parseFloat(a[sortField]) || 0, bv = parseFloat(b[sortField]) || 0;
        return sortDir === "asc" ? av - bv : bv - av;
      });
      setSortField(""); // Clear visual sort since it's now permanent
    }

    // Find their indexes in the main results array
    const currentMainIdx = baseResults.findIndex(r => r._key === currentItem._key);
    const targetMainIdx = baseResults.findIndex(r => r._key === targetItem._key);

    if (currentMainIdx !== -1 && targetMainIdx !== -1) {
      const newResults = [...baseResults];
      const temp = newResults[currentMainIdx];
      newResults[currentMainIdx] = newResults[targetMainIdx];
      newResults[targetMainIdx] = temp;
      setResults(newResults);
    }
  };

  const saveNewVersion = async (customLabel = null) => {
    if (!selectedStudent) return;
    const prefs = getActiveItems();
    setSaving(true);
    try {
      const res = await api.post("/preferences/drafts/save", {
        student_id: selectedStudent.id,
        list_type: examTab,
        filters_used: { 
          percentile: percentile ? parseFloat(percentile) : null,
          rank: rank ? parseFloat(rank) : null,
          percentile_plus_buffer: percPlusBuf ? parseFloat(percPlusBuf) : 0.0,
          percentile_minus_buffer: percMinusBuf ? parseFloat(percMinusBuf) : 5.0,
          rank_plus_buffer: rankPlusBuf ? parseFloat(rankPlusBuf) : 5000.0,
          rank_minus_buffer: rankMinusBuf ? parseFloat(rankMinusBuf) : 0.0,
          cities: selectedCities, 
          course_groups: selectedCourseGroups, 
          base_category: baseCategory,
          special_quotas: specialQuotas,
          enable_priority: enablePriority,
          print_rank: printRank,
          home_university: homeUniversity || null,
          gender: gender || "General",
          zone_basis: zoneBasis,
          zone_rank_upper: zoneRankUpper,
          zone_rank_lower: zoneRankLower,
          zone_perc_upper: zonePercUpper,
          zone_perc_lower_mode: zonePercLowerMode,
          zone_perc_lower_custom: zonePercLowerCustom
        },
        preferences: prefs.map((item, idx) => ({ ...item, preference_number: idx + 1 })),
        create_new_version: true,
        version_label: customLabel
      }, { headers: getAuthHeader() });
      
      toast.success(`Draft saved as version ${res.data.version_label}!`);
      await fetchStudentVersions(selectedStudent.id);
      setActiveVersionId(res.data.id);
      setShowSaveVersionModal(false);
      setShowDuplicateModal(false);
      setNewVersionName("");
    } catch {
      toast.error("Failed to save new version");
    } finally {
      setSaving(false);
    }
  };

  const updateExistingVersion = async () => {
    if (!selectedStudent || !activeVersionId) return;
    const prefs = getActiveItems();
    setSaving(true);
    try {
      const res = await api.post("/preferences/drafts/save", {
        student_id: selectedStudent.id,
        list_type: examTab,
        filters_used: { 
          percentile: percentile ? parseFloat(percentile) : null,
          rank: rank ? parseFloat(rank) : null,
          percentile_plus_buffer: percPlusBuf ? parseFloat(percPlusBuf) : 0.0,
          percentile_minus_buffer: percMinusBuf ? parseFloat(percMinusBuf) : 5.0,
          rank_plus_buffer: rankPlusBuf ? parseFloat(rankPlusBuf) : 5000.0,
          rank_minus_buffer: rankMinusBuf ? parseFloat(rankMinusBuf) : 0.0,
          cities: selectedCities, 
          course_groups: selectedCourseGroups, 
          base_category: baseCategory,
          special_quotas: specialQuotas,
          enable_priority: enablePriority,
          print_rank: printRank,
          home_university: homeUniversity || null,
          gender: gender || "General",
          zone_basis: zoneBasis,
          zone_rank_upper: zoneRankUpper,
          zone_rank_lower: zoneRankLower,
          zone_perc_upper: zonePercUpper,
          zone_perc_lower_mode: zonePercLowerMode,
          zone_perc_lower_custom: zonePercLowerCustom
        },
        preferences: prefs.map((item, idx) => ({ ...item, preference_number: idx + 1 })),
        version_id: activeVersionId,
        create_new_version: false
      }, { headers: getAuthHeader() });
      
      toast.success(`Version ${res.data.version_label} updated successfully!`);
      await fetchStudentVersions(selectedStudent.id);
      setShowSaveVersionModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update version");
    } finally {
      setSaving(false);
    }
  };

  // Save / Finalize / Export / Print (Task 3: triggered from top bar)
  const handleSaveDraft = async () => {
    if (!selectedStudent) { toast.error("Select a student first"); return; }
    const prefs = getActiveItems();
    if (prefs.length === 0) { toast.error("List is empty"); return; }

    if (activeVersionId) {
      const currentVer = studentVersions.find(v => v.id === activeVersionId);
      if (currentVer && currentVer.status === "FINALIZED") {
        // Finalized versions are read-only. Auto-save as new version!
        toast.info("Active version is finalized. Saving as a new version...");
        await saveNewVersion();
      } else {
        // Draft version: prompt update vs new version
        setNewVersionName("");
        setShowSaveVersionModal(true);
      }
    } else {
      // First save (from CET/JEE tab)
      await saveNewVersion();
    }
  };

  const handleOpenDuplicateModal = () => {
    if (!selectedStudent || !activeVersionId) return;
    const currentVer = studentVersions.find(v => v.id === activeVersionId);
    const currentLabel = currentVer ? currentVer.version_label : "Draft";
    setDuplicateVersionName(`${currentLabel} (Copy)`);
    setShowDuplicateModal(true);
  };

  const handleDuplicateVersion = async () => {
    if (!duplicateVersionName.trim()) {
      toast.error("Please enter a version name");
      return;
    }
    await saveNewVersion(duplicateVersionName.trim());
  };

  const handleFinalize = async () => {
    if (!selectedStudent) { toast.error("Select a student first"); return; }
    if (!activeVersionId) {
      toast.error("Please click 'Save Draft' first to save this list before finalizing.");
      return;
    }
    const currentVer = studentVersions.find(v => v.id === activeVersionId);
    if (currentVer && currentVer.status === "FINALIZED") {
      toast.error("This version is already finalized.");
      return;
    }
    
    if (!window.confirm(`Finalize version ${currentVer ? currentVer.version_label : 'draft'}? This will lock it from further editing.`)) return;
    
    setSaving(true);
    try {
      await api.post("/preferences/drafts/finalize", { 
        student_id: selectedStudent.id, 
        list_type: examTab,
        version_id: activeVersionId
      }, { headers: getAuthHeader() });
      
      toast.success("Version finalized successfully!");
      await fetchStudentVersions(selectedStudent.id);
    } catch (err) { 
      toast.error(err?.response?.data?.detail || "Finalization failed."); 
    } finally { 
      setSaving(false); 
    }
  };

  const handlePrint = () => {
    const prefs = isActiveVersionFinalized() ? getDisplayResults() : getActiveItems();
    if (prefs.length === 0) { toast.error("Nothing to print"); return; }
    const sn = selectedStudent?.name || "Student"; 
    const rn = selectedStudent?.registration_number || "";
    const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const rankLabel = examTab === 'CET' ? 'Rank' : 'AI Merit';
    
    const activeVer = studentVersions.find(v => v.id === activeVersionId);
    const versionText = activeVer ? `${activeVer.version_label} (${activeVer.list_type})` : examTab;
    const statusText = activeVer ? activeVer.status : "WORKING STATE";
    const percLabel = examTab === 'CET' ? 'MHT-CET Percentile' : 'JEE Percentile';
    const disclaimerText = examTab === 'JEE'
      ? 'ही Preference List विद्यार्थ्यांनी Google Form मध्ये दिलेली माहिती, JEE Main Percentile आणि मागील वर्षांच्या Cutoff च्या आधारे तयार करण्यात आलेली प्राथमिक (Primary) मार्गदर्शक यादी आहे. Final Admission Options व Preference List ही JEE Main All India Rank जाहीर झाल्यानंतर विद्यार्थ्याच्या Rank नुसार अद्ययावत करण्यात येईल.'
      : 'ही Preference List विद्यार्थ्यांनी Google Form मध्ये दिलेली माहिती, MHT-CET Percentile आणि मागील वर्षांच्या Cutoff च्या आधारे तयार करण्यात आलेली प्राथमिक (Primary) मार्गदर्शक यादी आहे. Final Admission Options व Preference List ही MHT-CET Rank जाहीर झाल्यानंतर विद्यार्थ्याच्या Rank नुसार अद्ययावत करण्यात येईल.';

    const studentPerc = percentile ? parseFloat(percentile) : null;
    const studentRank = rank ? parseFloat(rank) : null;

    const getAdmissionCategory = (item) => {
      if (item.manualZone) {
        if (item.manualZone === "Dream") return { label: "Dream", colorClass: "dream", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", lineBorder: "#c084fc" };
        if (item.manualZone === "Safe") return { label: "Safe", colorClass: "safe", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", lineBorder: "#34d399" };
        return { label: "Target", colorClass: "target", color: "#d97706", bg: "#fffbeb", border: "#fde68a", lineBorder: "#fbbf24" };
      }
      
      const studentRank = parseFloat(rank);
      const itemRank = parseFloat(item.Rank || item["AI Merit"]);
      const studentPerc = parseFloat(percentile);
      const itemPerc = parseFloat(item.Percentile || item["Percen Tile"]);

      // Determine basis
      const useRank = zoneBasis === 'rank';

      if (useRank && itemRank) {
        const upperLimit = parseFloat(zoneRankUpper) || 0;
        const lowerLimit = parseFloat(zoneRankLower) || 999999;

        if (itemRank < upperLimit) return { label: "Dream", colorClass: "dream", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", lineBorder: "#c084fc" };
        else if (itemRank <= lowerLimit) return { label: "Target", colorClass: "target", color: "#d97706", bg: "#fffbeb", border: "#fde68a", lineBorder: "#fbbf24" };
        else return { label: "Safe", colorClass: "safe", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", lineBorder: "#34d399" };
      } else if (itemPerc) {
        let targetLowerBuf = 5.0;
        if (studentPerc) {
          if (studentPerc >= 98) targetLowerBuf = 1.0;
          else if (studentPerc >= 90) targetLowerBuf = 2.5;
          else if (studentPerc >= 80) targetLowerBuf = 4.0;
          else if (studentPerc >= 70) targetLowerBuf = 6.0;
          else targetLowerBuf = 10.0;
        }

        const upperLimit = parseFloat(zonePercUpper) || 100.0;
        const lowerLimit = zonePercLowerMode === 'custom'
          ? (parseFloat(zonePercLowerCustom) || 0.0)
          : (studentPerc ? studentPerc - targetLowerBuf : 0.0);

        if (itemPerc > upperLimit) return { label: "Dream", colorClass: "dream", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", lineBorder: "#c084fc" };
        else if (itemPerc >= lowerLimit) return { label: "Target", colorClass: "target", color: "#d97706", bg: "#fffbeb", border: "#fde68a", lineBorder: "#fbbf24" };
        else return { label: "Safe", colorClass: "safe", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", lineBorder: "#34d399" };
      }
      return { label: "Target", colorClass: "target", color: "#d97706", bg: "#fffbeb", border: "#fde68a", lineBorder: "#fbbf24" };
    };
    
    const showRankColumn = printRank;
    const showBranchRankColumn = printBranchRank;
    const rankHeader = showRankColumn ? `<th style="width: 8%;">${rankLabel}</th>` : '';
    const branchRankHeader = showBranchRankColumn ? `<th style="width: 8%;">Branch Rank</th>` : '';
    const instWidth = showRankColumn ? '32%' : '40%';
    const branchWidth = showRankColumn ? '20%' : '20%';

    const tableRows = prefs.map((p, i) => {
      const zone = getAdmissionCategory(p);
      const rankCell = showRankColumn ? `<td class="rank-cell">${examTab === 'CET' ? (p["Rank"] || "-") : (p["AI Merit"] || "-")}</td>` : '';
      const branchRankCell = showBranchRankColumn ? `<td class="rank-cell">${p["Branch Rank"] || "-"}</td>` : '';
      return `
        <tr>
          <td class="pref-num" style="text-align: center; border-left: 4px solid ${zone.lineBorder} !important; font-weight: 800;">${i + 1}</td>
          <td class="inst-name">${p["INSTITUTE"] || p["Institute Name"] || ""}</td>
          <td>${p["BRANCH NAME"] || p["Course Name"] || ""}</td>
          ${branchRankCell}
          <td>${p["CITY"] || ""}</td>
          ${rankCell}
          <td class="percentile-cell">${p["Percentile"] || p["Percen Tile"] || "-"}</td>
          <td>
            <span class="pdf-zone-badge ${zone.colorClass}" style="color: ${zone.color}; background: ${zone.bg}; border: 1px solid ${zone.border}; padding: 3px 8px; border-radius: 50px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block;">${zone.label}</span>
          </td>
          <td>
            <span class="choice-code">${p["Branch Code"] || p["Choice Code"] || "-"}</span>
          </td>
        </tr>
      `;
    }).join('');

    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Preference List - ${sn}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;700&display=swap');
          
          body {
            font-family: 'Outfit', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            padding: 30px;
            color: #1e293b;
            background: #fff;
            margin: 0;
            line-height: 1.4;
          }
          
          .brand-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid #0284c7;
            padding-bottom: 15px;
            margin-bottom: 25px;
          }
          .brand-title {
            font-size: 24px;
            font-weight: 800;
            color: #0f172a;
            letter-spacing: -0.5px;
            margin: 0;
          }
          .brand-title span {
            color: #0284c7;
          }
          .brand-subtitle {
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            color: #64748b;
            font-weight: 700;
            margin-top: 2px;
          }
          .doc-type {
            text-align: right;
          }
          .doc-type-badge {
            background: #e0f2fe;
            color: #0369a1;
            padding: 4px 12px;
            border-radius: 50px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            display: inline-block;
          }
          .doc-date {
            font-size: 12px;
            color: #64748b;
            margin-top: 5px;
            font-weight: 500;
          }
          
          .student-card {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 15px 20px;
            margin-bottom: 30px;
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
          }
          .info-group {
            display: flex;
            flex-direction: column;
          }
          .info-label {
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #64748b;
            font-weight: 700;
            margin-bottom: 4px;
          }
          .info-value {
            font-size: 14px;
            color: #e11d48;
            font-weight: 700;
          }
          .status-badge {
            display: inline-block;
            width: fit-content;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
          }
          .status-badge.approved {
            background: #dcfce7;
            color: #15803d;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
            margin-top: 10px;
          }
          th {
            background: #0f172a;
            color: #fff;
            font-weight: 700;
            text-transform: uppercase;
            font-size: 10px;
            letter-spacing: 0.8px;
            padding: 12px 10px;
            text-align: left;
            border: none;
          }
          th:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          th:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
          td {
            padding: 10px;
            border-bottom: 1px solid #e2e8f0;
            vertical-align: middle;
            color: #334155;
          }
          tr:nth-child(even) td {
            background: #f8fafc;
          }
          
          .choice-code {
            font-family: 'JetBrains Mono', monospace;
            font-size: 12px;
            font-weight: 700;
            color: #0284c7;
            background: #f0f9ff;
            border: 1px dashed #bae6fd;
            padding: 4px 8px;
            border-radius: 6px;
            display: inline-block;
            letter-spacing: 0.5px;
          }
          
          .rank-cell, .percentile-cell {
            font-weight: 600;
            color: #0f172a;
          }
          .inst-name {
            font-weight: 600;
            color: #0f172a;
          }
          .pref-num {
            font-weight: 700;
            color: #475569;
          }
          
          tr {
            page-break-inside: avoid;
          }
          
          .print-footer {
            margin-top: 40px;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            color: #94a3b8;
            font-weight: 500;
          }
          .disclaimer-box {
            background: #fffbeb;
            border: 1px solid #fde68a;
            border-radius: 8px;
            padding: 12px 15px;
            margin-top: 25px;
            font-size: 11px;
            color: #b45309;
            line-height: 1.5;
            page-break-inside: avoid;
          }
          .disclaimer-title {
            font-weight: 700;
            margin-bottom: 4px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-size: 10px;
          }
        </style>
      </head>
      <body>
        
        <div class="brand-header">
          <div>
            <h1 class="brand-title">Admissions <span>Made Easy</span></h1>
            <div class="brand-subtitle">Smart Choice Entry Portal</div>
          </div>
          <div class="doc-type">
            <div class="doc-type-badge">${versionText} Preference List</div>
            <div class="doc-date">${currentDate}</div>
          </div>
        </div>

        <div class="student-card">
          <div class="info-group">
            <div class="info-label">Student Name</div>
            <div class="info-value">${sn}</div>
          </div>
          <div class="info-group">
            <div class="info-label">Registration No</div>
            <div class="info-value">${rn}</div>
          </div>
          <div class="info-group">
            <div class="info-label">Student Category</div>
            <div class="info-value">${selectedStudent.category || "OPEN"}</div>
          </div>
          <div class="info-group">
            <div class="info-label">Special Reservation</div>
            <div class="info-value">${selectedStudent.special_reservation || "-"}</div>
          </div>
          <div class="info-group">
            <div class="info-label">${percLabel}</div>
            <div class="info-value">${percentile ? `${percentile}%` : "-"}</div>
          </div>
          <div class="info-group">
            <div class="info-label">Student Rank</div>
            <div class="info-value">${rank ? Math.round(parseFloat(rank)).toLocaleString() : "-"}</div>
          </div>
          <div class="info-group">
            <div class="info-label">Counselor In-Charge</div>
            <div class="info-value">${user.name}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 6%; text-align: center;">Pref #</th>
              <th style="width: ${instWidth};">Institute</th>
              <th style="width: ${branchWidth};">Branch / Course</th>
              <th style="width: 8%;">City</th>
              ${rankHeader}
              <th style="width: 8%;">Percentile</th>
              <th style="width: 10%;">Category</th>
              <th style="width: 8%;">Choice Code</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>

        <div class="disclaimer-box">
          <div class="disclaimer-title">⚠️ Disclaimer / महत्त्वाचे प्रसिद्धीपत्रक</div>
          ${disclaimerText}
        </div>

        <div class="print-footer">
          <div>Generated via Admissions Made Easy • Personal Counselor Preference Portal</div>
          <div>Page 1 of 1</div>
        </div>

      </body>
      </html>
    `;
    const w = window.open("", "_blank"); w.document.write(printHTML); w.document.close(); w.print();
  };

  const handleDeleteVersion = async () => {
    if (!selectedStudent || !activeVersionId) return;
    
    const activeVer = studentVersions.find(v => v.id === activeVersionId);
    const verLabel = activeVer ? `${activeVer.list_type} (${activeVer.version_label || `V${activeVer.version}`})` : "this version";
    
    if (!window.confirm(`Are you sure you want to delete version ${verLabel}? This action cannot be undone.`)) {
      return;
    }
    
    setSaving(true);
    try {
      await api.delete(`/preferences/drafts/${activeVersionId}`, { headers: getAuthHeader() });
      toast.success("Version deleted successfully!");
      
      // Update versions list
      await fetchStudentVersions(selectedStudent.id);
      
      // Revert to default tab
      setActiveVersionId(null);
      loadStudentState(selectedStudent.id, examTab);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete version");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBelow = () => {
    const displayList = getDisplayResults();
    if (displayList.length === 0) return;
    
    const numStr = window.prompt(`Enter row number to keep (everything below this row will be deleted, max ${displayList.length}):`);
    if (numStr === null) return; // cancelled
    
    const num = parseInt(numStr, 10);
    if (isNaN(num) || num < 1 || num > displayList.length) {
      toast.error(`Invalid row number. Please enter a number between 1 and ${displayList.length}.`);
      return;
    }

    const itemsToRemove = displayList.slice(num);
    if (itemsToRemove.length === 0) {
      toast.info("No options to delete below this row.");
      return;
    }

    if (window.confirm(`Are you sure you want to delete all ${itemsToRemove.length} options below row #${num}?`)) {
      const truncated = displayList.slice(0, num);
      setResults(truncated);
      setRemovedIds(new Set());
      toast.success(`Deleted all options below row #${num}`);
    }
  };

  const handleExportCSV = () => {
    const prefs = getActiveItems();
    if (prefs.length === 0) { toast.error("Nothing to export"); return; }
    const h = examTab==="CET" ? ["#","Institute","Branch","Branch Rank","Branch Group","Category","City","Rank","Percentile","Choice Code"] : ["#","Institute","Course","Branch Rank","Course Group","City","AI Merit","Percentile","Choice Code"];
    const rows = prefs.map((p,i) => examTab==="CET" ? [i+1,p["INSTITUTE"]||"",p["BRANCH NAME"]||"",p["Branch Rank"]||"-",p["BRANCH GROUP"]||"",p["CATEGORY"]||"",p["CITY"]||"",p["Rank"]||"",p["Percentile"]||"",p["Branch Code"]||""] : [i+1,p["Institute Name"]||"",p["Course Name"]||"",p["Branch Rank"]||"-",p["Course Group"]||"",p["CITY"]||"",p["AI Merit"]||"",p["Percen Tile"]||"",p["Choice Code"]||""]);
    let csv = h.join(",") + "\n"; rows.forEach(r => { csv += r.map(c=>`"${c}"`).join(",") + "\n"; });
    const blob = new Blob([csv],{type:"text/csv"}); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `pref_${examTab}_${Date.now()}.csv`; a.click();
    toast.success("Exported!");
  };

  const handleCreateStudent = async () => {
    try {
      const res = await api.post("/preferences/students", newStudentForm, { headers: getAuthHeader() });
      toast.success(`Student "${res.data.name}" created!`);
      setShowStudentModal(false); setNewStudentForm({ registration_number: "", name: "", mobile: "", password: "", category: "OPEN", special_reservation: "", branch: "AME Pune" });
      fetchStudents();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const handleOpenEditStudent = (e, s) => {
    e.stopPropagation();
    setEditStudentForm({
      id: s.id,
      registration_number: s.registration_number || "",
      name: s.name || "",
      mobile: s.mobile || "",
      password: "", // leave blank unless they want to change
      category: s.category || "OPEN",
      special_reservation: s.special_reservation || "",
      branch: s.branch || "AME Pune"
    });
    setShowEditStudentModal(true);
  };

  const handleEditStudentSubmit = async () => {
    try {
      await api.put(`/preferences/students/${editStudentForm.id}`, editStudentForm, { headers: getAuthHeader() });
      toast.success("Student updated successfully!");
      setShowEditStudentModal(false);
      fetchStudents();
      if (selectedStudent && selectedStudent.id === editStudentForm.id) {
        setSelectedStudent({ ...selectedStudent, ...editStudentForm });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    }
  };

  const handleDeleteStudent = async (e, s) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete student "${s.name}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/preferences/students/${s.id}`, { headers: getAuthHeader() });
      toast.success("Student deleted");
      if (selectedStudent && selectedStudent.id === s.id) {
        setSelectedStudent(null);
      }
      fetchStudents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  const logout = () => { localStorage.removeItem(PREF_TOKEN_KEY); localStorage.removeItem(PREF_USER_KEY); navigate("/engineering-admission/portal-login"); };

  if (!user) return null;
  const displayResults = getDisplayResults();

  return (
    <>
      <Helmet><title>Counselor Workspace | Preference Portal</title></Helmet>
      <style>{`
        .pcw{min-height:100vh;background:#0f172a;color:#e2e8f0;font-family:'Inter',sans-serif;display:flex;flex-direction:column}

        /* TOP BAR — Task 3: action buttons here */
        .pcw-top{display:flex;align-items:center;justify-content:space-between;padding:0.6rem 1.25rem;background:rgba(30,41,59,0.95);backdrop-filter:blur(12px);border-bottom:1px solid rgba(255,255,255,0.06);position:sticky;top:0;z-index:50;gap:0.5rem;flex-wrap:wrap}
        .pcw-top-left{display:flex;align-items:center;gap:0.6rem}
        .pcw-top-logo{width:34px;height:34px;border-radius:9px;background:linear-gradient(135deg,#0ea5e9,#0284c7);display:flex;align-items:center;justify-content:center;color:#fff;font-size:0.85rem}
        .pcw-top h2{margin:0;font-size:0.95rem;color:#fff;font-weight:700;display:flex;align-items:center;}
        .pcw-top-center{display:flex;align-items:center;gap:0.4rem;flex-wrap:wrap}
        .pcw-topbtn{padding:0.35rem 0.85rem;border-radius:8px;border:none;cursor:pointer;font-weight:600;font-size:0.72rem;font-family:inherit;transition:all 0.15s;display:inline-flex;align-items:center;gap:4px;white-space:nowrap}
        .pcw-topbtn.save{background:rgba(14,165,233,0.12);color:#38bdf8;border:1px solid rgba(14,165,233,0.25)}
        .pcw-topbtn.save:hover{background:rgba(14,165,233,0.22)}
        .pcw-topbtn.finalize{background:rgba(16,185,129,0.12);color:#34d399;border:1px solid rgba(16,185,129,0.25)}
        .pcw-topbtn.finalize:hover{background:rgba(16,185,129,0.22)}
        .pcw-topbtn.export{background:rgba(245,158,11,0.12);color:#fbbf24;border:1px solid rgba(245,158,11,0.25)}
        .pcw-topbtn.export:hover{background:rgba(245,158,11,0.22)}
        .pcw-topbtn.print{background:rgba(168,85,247,0.12);color:#c084fc;border:1px solid rgba(168,85,247,0.25)}
        .pcw-topbtn.print:hover{background:rgba(168,85,247,0.22)}
        .pcw-top-right{display:flex;align-items:center;gap:0.5rem}
        .pcw-chip{padding:0.25rem 0.65rem;border-radius:50px;font-size:0.72rem;font-weight:600}
        .pcw-chip.user{background:rgba(14,165,233,0.1);border:1px solid rgba(14,165,233,0.25);color:#38bdf8}
        .pcw-chip.student{background:rgba(16,185,129,0.1);border:1px solid rgba(16,185,129,0.25);color:#34d399}
        .pcw-chip.count{background:rgba(245,158,11,0.1);border:1px solid rgba(245,158,11,0.25);color:#fbbf24}
        .pcw-logout{padding:0.3rem 0.65rem;background:rgba(239,68,68,0.1);border:1px solid rgba(239,68,68,0.2);border-radius:6px;color:#f87171;cursor:pointer;font-size:0.72rem;font-weight:600;font-family:inherit}

        /* BODY — Task 2: collapsible sidebar; Task 4: full-width main */
        .pcw-body{display:flex;flex:1;overflow:hidden}

        /* Sidebar toggle button */
        .pcw-sb-toggle{position:absolute;top:50%;transform:translateY(-50%);width:20px;height:44px;background:rgba(14,165,233,0.15);border:1px solid rgba(14,165,233,0.3);border-radius:0 8px 8px 0;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#38bdf8;font-size:0.65rem;z-index:40;transition:all 0.2s;left:0}
        .pcw-sb-toggle:hover{background:rgba(14,165,233,0.25)}
        .pcw-sb-toggle.shifted{left:240px}

        /* Sidebar */
        .pcw-sidebar{width:240px;min-width:240px;background:rgba(15,23,42,0.95);border-right:1px solid rgba(255,255,255,0.06);display:flex;flex-direction:column;overflow:hidden;transition:all 0.3s cubic-bezier(0.4,0,0.2,1);position:relative;z-index:35}
        .pcw-sidebar.collapsed{width:0;min-width:0;border-right:none;overflow:hidden}
        .pcw-sb-head{padding:0.85rem;border-bottom:1px solid rgba(255,255,255,0.06)}
        .pcw-sb-head h4{margin:0 0 0.6rem;font-size:0.78rem;color:#94a3b8;font-weight:700;text-transform:uppercase;letter-spacing:1px}
        .pcw-sb-search{width:100%;padding:0.45rem 0.65rem;border-radius:8px;background:rgba(0,0,0,0.3);border:1px solid rgba(255,255,255,0.08);color:#fff;outline:none;font-size:0.8rem;box-sizing:border-box;font-family:inherit}
        .pcw-sb-search:focus{border-color:#0ea5e9}
        .pcw-sb-add{width:100%;padding:0.4rem;margin-top:0.4rem;border-radius:8px;background:rgba(14,165,233,0.1);border:1px solid rgba(14,165,233,0.25);color:#38bdf8;cursor:pointer;font-size:0.75rem;font-weight:600;font-family:inherit}
        .pcw-sb-list{flex:1;overflow-y:auto;padding:0.4rem}
        .pcw-sb-item{padding:0.5rem 0.65rem;border-radius:8px;cursor:pointer;margin-bottom:3px;transition:all 0.15s}
        .pcw-sb-item:hover{background:rgba(255,255,255,0.04)}
        .pcw-sb-item.active{background:rgba(14,165,233,0.12);border:1px solid rgba(14,165,233,0.3)}
        .pcw-sb-item-name{font-size:0.82rem;font-weight:600;color:#f1f5f9}
        .pcw-sb-item-reg{font-size:0.68rem;color:#64748b;font-family:monospace}

        /* MAIN workspace — full width */
        .pcw-main{flex:1;overflow-y:auto;padding:1rem 1.25rem;display:flex;flex-direction:column;gap:0.85rem;position:relative}
        .pcw-exam-tabs{display:flex;gap:0.4rem}
        .pcw-exam-tab{padding:0.45rem 1.25rem;border-radius:10px;background:rgba(255,255,255,0.03);border:1.5px solid rgba(255,255,255,0.08);color:#64748b;cursor:pointer;font-weight:700;font-size:0.82rem;transition:all 0.2s;font-family:inherit}
        .pcw-exam-tab:hover{color:#cbd5e1}
        .pcw-exam-tab.active{background:rgba(14,165,233,0.12);border-color:#0ea5e9;color:#38bdf8}

        /* Filter Summary */
        .pcw-summary{background:rgba(14,165,233,0.08);border:1px solid rgba(14,165,233,0.2);border-radius:12px;padding:0.75rem 1rem;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:0.5rem;margin-bottom:0.25rem}
        .pcw-summary-left{display:flex;align-items:center;gap:0.75rem;flex-wrap:wrap;font-size:0.8rem;color:#cbd5e1}
        .pcw-summary-item{background:rgba(0,0,0,0.3);padding:0.25rem 0.6rem;border-radius:6px;border:1px solid rgba(255,255,255,0.06);display:flex;align-items:center;gap:4px}
        .pcw-summary-item strong{color:#38bdf8;font-weight:600}
        .pcw-edit-filters-btn{background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.15);color:#fff;padding:0.35rem 0.85rem;border-radius:8px;font-size:0.75rem;font-weight:600;cursor:pointer;transition:all 0.2s}
        .pcw-edit-filters-btn:hover{background:rgba(255,255,255,0.15)}

        /* Searchable Multi-Select Custom CSS */
        .pcw-sms-wrapper{position:relative;flex:1;min-width:260px}
        .pcw-sms-wrapper label{display:block;font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:0.4rem}
        .pcw-sms-box{min-height:42px;border:1px solid rgba(255,255,255,0.1);background:rgba(15,23,42,0.6);border-radius:10px;padding:4px 30px 4px 8px;display:flex;align-items:center;flex-wrap:wrap;cursor:text;position:relative;transition:all 0.2s;box-shadow:inset 0 2px 4px rgba(0,0,0,0.2)}
        .pcw-sms-box.open{border-color:#0ea5e9;background:rgba(15,23,42,0.8);box-shadow:0 0 0 2px rgba(14,165,233,0.2), inset 0 2px 4px rgba(0,0,0,0.2)}
        .pcw-sms-tags{display:flex;flex-wrap:wrap;gap:6px;flex:1;align-items:center}
        .pcw-sms-tag{background:rgba(14,165,233,0.15);border:1px solid rgba(14,165,233,0.3);color:#38bdf8;padding:3px 8px;border-radius:6px;font-size:0.75rem;font-weight:600;display:flex;align-items:center;gap:6px}
        .pcw-sms-tag i{cursor:pointer;opacity:0.7;transition:opacity 0.2s}
        .pcw-sms-tag i:hover{opacity:1;color:#f87171}
        .pcw-sms-priority{width:26px;padding:1px;border-radius:4px;background:rgba(0,0,0,0.4);border:1px solid rgba(14,165,233,0.4);color:#fff;text-align:center;font-size:0.7rem;font-family:inherit;outline:none;margin-left:2px}
        .pcw-sms-input{background:transparent;border:none;outline:none;color:#fff;font-size:0.85rem;min-width:120px;flex:1;padding:4px;font-family:inherit}
        .pcw-sms-arrow{position:absolute;right:12px;top:50%;transform:translateY(-50%);color:#64748b;pointer-events:none}
        .pcw-sms-menu{position:absolute;top:calc(100% + 4px);left:0;right:0;background:#1e293b;border:1px solid rgba(255,255,255,0.1);border-radius:10px;max-height:220px;overflow-y:auto;z-index:60;box-shadow:0 10px 25px rgba(0,0,0,0.5)}
        .pcw-sms-option{padding:0.6rem 0.8rem;display:flex;align-items:center;gap:8px;cursor:pointer;font-size:0.8rem;color:#e2e8f0;transition:background 0.15s;border-bottom:1px solid rgba(255,255,255,0.03)}
        .pcw-sms-option:hover{background:rgba(255,255,255,0.05)}
        .pcw-sms-option.selected{background:rgba(14,165,233,0.08);color:#38bdf8}
        .pcw-sms-checkbox{width:16px;height:16px;border-radius:4px;border:1px solid rgba(255,255,255,0.2);display:flex;align-items:center;justify-content:center;font-size:0.6rem;color:#0ea5e9}
        .pcw-sms-option.selected .pcw-sms-checkbox{border-color:#0ea5e9;background:rgba(14,165,233,0.1)}
        .pcw-sms-nores{padding:1rem;text-align:center;color:#64748b;font-size:0.8rem}

        /* Filter panel - Glassmorphism */
        .pcw-filters{background:rgba(30,41,59,0.4);backdrop-filter:blur(16px);border:1px solid rgba(255,255,255,0.08);border-radius:16px;padding:1.25rem;box-shadow:0 8px 32px rgba(0,0,0,0.2)}
        .pcw-filter-row{display:flex;gap:1.25rem;align-items:flex-start;margin-bottom:1rem}
        .pcw-perc-wrap{width:180px}
        .pcw-perc-wrap label{display:block;font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#94a3b8;margin-bottom:0.4rem}
        .pcw-perc-input{width:100%;height:42px;padding:0 0.8rem;border-radius:10px;background:rgba(15,23,42,0.6);border:1px solid rgba(255,255,255,0.1);color:#fff;outline:none;font-size:0.9rem;box-sizing:border-box;font-family:inherit;box-shadow:inset 0 2px 4px rgba(0,0,0,0.2);transition:all 0.2s}
        .pcw-perc-input:focus{border-color:#0ea5e9;box-shadow:0 0 0 2px rgba(14,165,233,0.2), inset 0 2px 4px rgba(0,0,0,0.2)}
        .pcw-select-input{width:100%;height:42px;padding:0 0.8rem;border-radius:10px;background:rgba(15,23,42,0.6);border:1px solid rgba(255,255,255,0.1);color:#fff;outline:none;font-size:0.85rem;box-sizing:border-box;font-family:inherit;box-shadow:inset 0 2px 4px rgba(0,0,0,0.2);transition:all 0.2s;cursor:pointer;appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 0.75rem center}
        .pcw-select-input:focus{border-color:#0ea5e9;box-shadow:0 0 0 2px rgba(14,165,233,0.2), inset 0 2px 4px rgba(0,0,0,0.2)}
        .pcw-select-input option{background:#1e293b;color:#e2e8f0}
        
        /* Actions */
        .pcw-filter-actions{display:flex;gap:0.75rem;align-items:center;justify-content:flex-end;margin-top:0.5rem}
        
        /* Modern Switch */
        .pcw-switch-wrap{display:flex;align-items:center;gap:8px;color:#94a3b8;font-size:0.75rem;font-weight:600;cursor:pointer}
        .pcw-switch{position:relative;width:40px;height:22px;background:rgba(255,255,255,0.1);border-radius:50px;transition:all 0.3s}
        .pcw-switch::after{content:'';position:absolute;top:2px;left:2px;width:18px;height:18px;background:#fff;border-radius:50%;transition:all 0.3s}
        .pcw-switch.on{background:#0ea5e9}
        .pcw-switch.on::after{transform:translateX(18px)}
        
        .pcw-generate-btn{padding:0.6rem 1.75rem;border-radius:10px;background:linear-gradient(135deg,#0ea5e9,#0284c7);color:#fff;border:none;cursor:pointer;font-weight:700;font-size:0.9rem;font-family:inherit;box-shadow:0 4px 16px rgba(14,165,233,0.4);transition:all 0.2s}
        .pcw-generate-btn:hover{transform:translateY(-2px);box-shadow:0 6px 20px rgba(14,165,233,0.5)}
        .pcw-generate-btn:disabled{opacity:0.5;cursor:not-allowed;transform:none}

        /* Results table — grid style (Task 4) */
        .pcw-results{background:rgba(30,41,59,0.5);border:1px solid rgba(255,255,255,0.06);border-radius:14px;padding:0.85rem;display:flex;flex-direction:column;flex:1;overflow:hidden}
        .pcw-results-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:0.6rem;flex-wrap:wrap;gap:0.4rem}
        .pcw-results-header h4{margin:0;font-size:0.88rem;color:#f1f5f9;font-weight:700}
        .pcw-results-count{font-size:0.75rem;color:#64748b}
        .pcw-result-search{padding:0.35rem 0.7rem;border-radius:6px;background:rgba(0,0,0,0.2);border:1px solid rgba(255,255,255,0.08);color:#fff;outline:none;font-size:0.78rem;width:240px;font-family:inherit;box-sizing:border-box}
        .pcw-rtable-wrap{flex:1;overflow-y:auto;border-radius:8px}
        
        .pcw-rtable{display:flex;flex-direction:column;width:100%;font-size:0.76rem;min-width:850px}
        
        .pcw-rtable-header{display:grid;background:#1e293b;position:sticky;top:0;z-index:2;border-bottom:1px solid rgba(255,255,255,0.06);border-left:3px solid transparent}
        .pcw-rtable-header.cet{grid-template-columns:42px 2.5fr 1.8fr 1fr 1fr 1fr 1fr 1fr 70px}
        .pcw-rtable-header.jee{grid-template-columns:42px 2.5fr 1.8fr 1fr 1fr 1fr 1fr 70px}
        
        .pcw-header-cell{padding:0.55rem 0.5rem;text-align:left;color:#64748b;font-size:0.66rem;text-transform:uppercase;letter-spacing:1px;font-weight:700;white-space:normal;word-break:break-word;display:flex;align-items:center}
        .pcw-header-cell.sortable{cursor:pointer}
        .pcw-header-cell.sortable:hover{color:#94a3b8}
        
        .pcw-rtable-body{display:flex;flex-direction:column}
        
        .pcw-row-interactive{display:grid;border-bottom:1px solid rgba(255,255,255,0.03);align-items:center;transition:all 0.2s;user-select:none;-webkit-user-select:none;cursor:pointer;border-left:3px solid transparent}
        .pcw-row-interactive.cet{grid-template-columns:42px 2.5fr 1.8fr 1fr 1fr 1fr 1fr 1fr 70px}
        .pcw-row-interactive.jee{grid-template-columns:42px 2.5fr 1.8fr 1fr 1fr 1fr 1fr 70px}
        
        .pcw-row-interactive.dream { border-left: 3px solid #c084fc !important; }
        .pcw-row-interactive.target { border-left: 3px solid #fbbf24 !important; }
        .pcw-row-interactive.safe { border-left: 3px solid #34d399 !important; }
        
        .pcw-zone-badge { display: inline-flex; align-items: center; justify-content: center; padding: 2px 6px; border-radius: 50px; font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-left: 6px; }
        .pcw-zone-badge.dream { background: rgba(168, 85, 247, 0.12); color: #c084fc; border: 1px solid rgba(168, 85, 247, 0.25); }
        .pcw-zone-badge.target { background: rgba(245, 158, 11, 0.12); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.25); }
        .pcw-zone-badge.safe { background: rgba(16, 185, 129, 0.12); color: #34d399; border: 1px solid rgba(16, 185, 129, 0.25); }
        
        .pcw-row-interactive:hover{background:rgba(255,255,255,0.04)}
        .pcw-row-interactive.selected-move{background:rgba(14,165,233,0.15);border:1px solid #0ea5e9;box-shadow:0 0 12px rgba(14,165,233,0.3);position:relative;z-index:5;animation:pcw-pulse 2s infinite}
        
        @keyframes pcw-pulse {
          0% { box-shadow: 0 0 8px rgba(14,165,233,0.3); }
          50% { box-shadow: 0 0 16px rgba(14,165,233,0.6); }
          100% { box-shadow: 0 0 8px rgba(14,165,233,0.3); }
        }
        
        .pcw-cell{padding:0.6rem 0.5rem;white-space:normal;word-break:break-word;height:auto;display:flex;align-items:center}
        
        .pcw-move-btn { background: transparent; border: none; color: #94a3b8; cursor: pointer; padding: 2px 4px; border-radius: 4px; transition: all 0.15s; display: flex; align-items: center; justify-content: center; opacity: 0.7; }
        .pcw-move-btn:hover:not(:disabled) { background: rgba(255,255,255,0.08); color: #38bdf8; opacity: 1; }
        .pcw-move-btn:disabled { opacity: 0.25; cursor: not-allowed; }
        
        .pcw-remove-btn{background:transparent;border:none;color:#ef4444;cursor:pointer;padding:4px 8px;border-radius:4px;opacity:0.7;transition:opacity 0.2s;display:flex;align-items:center;justify-content:center}
        .pcw-row-interactive:hover .pcw-remove-btn{opacity:1}
        .pcw-remove-btn:hover{background:rgba(239,68,68,0.1)}
        .pcw-empty{text-align:center;padding:2rem 1rem;color:#475569;font-size:0.85rem}

        .pcw-bulk-delete-btn{padding:0.35rem 0.75rem;border-radius:6px;background:rgba(239,68,68,0.12);border:1px solid rgba(239,68,68,0.25);color:#fca5a5;outline:none;font-size:0.76rem;cursor:pointer;font-family:inherit;transition:all 0.15s;display:flex;align-items:center}
        .pcw-bulk-delete-btn:hover{background:rgba(239,68,68,0.22);color:#fff;border-color:#ef4444}

        /* Modal */
        .pcw-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.6);backdrop-filter:blur(4px);z-index:100;display:flex;align-items:center;justify-content:center}
        .pcw-modal{background:#1e293b;border-radius:18px;padding:1.75rem;width:100%;max-width:380px;border:1px solid rgba(255,255,255,0.08)}
        .pcw-modal h3{margin:0 0 1.1rem;color:#fff;font-size:1.05rem;font-weight:700}
        .pcw-modal label{display:block;margin-bottom:0.3rem;font-size:0.75rem;color:#94a3b8;font-weight:600}
        .pcw-modal input{width:100%;padding:0.45rem 0.7rem;border-radius:8px;background:rgba(0,0,0,0.25);border:1px solid rgba(255,255,255,0.08);color:#fff;margin-bottom:0.75rem;outline:none;font-size:0.85rem;box-sizing:border-box;font-family:inherit}
        .pcw-modal input:focus{border-color:#0ea5e9}
        .pcw-modal-btns{display:flex;gap:0.5rem;justify-content:flex-end}
        .pcw-modal-cancel{padding:0.4rem 0.85rem;border-radius:8px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);color:#94a3b8;cursor:pointer;font-family:inherit;font-weight:600}
        .pcw-modal-ok{padding:0.4rem 0.85rem;border-radius:8px;background:#0ea5e9;border:none;color:#fff;cursor:pointer;font-family:inherit;font-weight:600}

        /* Manual Add Styles */
        .pcw-manual-add-wrap{position:relative;display:flex;align-items:center}
        .pcw-add-college-btn{padding:0.35rem 0.75rem;border-radius:6px;background:rgba(16,185,129,0.12);border:1px solid rgba(16,185,129,0.25);color:#a7f3d0;outline:none;font-size:0.76rem;cursor:pointer;font-family:inherit;transition:all 0.15s;display:flex;align-items:center}
        .pcw-add-college-btn:hover{background:rgba(16,185,129,0.22);color:#fff;border-color:#10b981}
        .pcw-manual-add-dropdown{position:absolute;top:100%;right:0;width:380px;background:#1e293b;border:1px solid rgba(255,255,255,0.08);box-shadow:0 12px 28px rgba(0,0,0,0.5);border-radius:10px;z-index:90;max-height:280px;overflow-y:auto;margin-top:0.4rem;display:flex;flex-direction:column}
        .pcw-manual-add-option{padding:0.6rem 0.75rem;border-bottom:1px solid rgba(255,255,255,0.04);cursor:pointer;transition:all 0.15s;display:flex;flex-direction:column;gap:0.15rem;text-align:left}
        .pcw-manual-add-option:last-child{border-bottom:none}
        .pcw-manual-add-option:hover{background:rgba(14,165,233,0.1)}
        .pcw-manual-add-option strong{color:#f8fafc;font-size:0.8rem;line-height:1.2;font-weight:600}
        .pcw-manual-add-option span{color:#94a3b8;font-size:0.7rem}
        .pcw-manual-add-meta{display:flex;gap:0.4rem;color:#0ea5e9;font-weight:500}

        /* Scroll-to-Top Floating Button */
        .pcw-scroll-top-btn{position:fixed;bottom:2rem;right:2rem;width:48px;height:48px;border-radius:50%;background:linear-gradient(135deg,#0ea5e9,#0284c7);border:2px solid rgba(255,255,255,0.15);color:#fff;font-size:1.1rem;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 6px 24px rgba(14,165,233,0.45),0 2px 8px rgba(0,0,0,0.3);z-index:100;transition:all 0.25s ease;backdrop-filter:blur(8px);animation:pcw-fade-in 0.3s ease}
        .pcw-scroll-top-btn:hover{transform:translateY(-3px) scale(1.08);box-shadow:0 8px 32px rgba(14,165,233,0.6),0 4px 12px rgba(0,0,0,0.4)}
        .pcw-scroll-top-btn:active{transform:translateY(-1px) scale(1.02)}
        @keyframes pcw-fade-in{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}

        @media(max-width:768px){
          .pcw-sidebar{position:fixed;left:0;top:0;bottom:0;z-index:60;width:260px}
          .pcw-sidebar.collapsed{transform:translateX(-260px)}
          .pcw-sb-toggle{position:fixed;z-index:61}
          .pcw-sb-toggle.shifted{left:260px}
          .pcw-top-center{display:none}
          .pcw-scroll-top-btn{bottom:1.25rem;right:1.25rem;width:42px;height:42px;font-size:1rem}
        }
      `}</style>

      <div className="pcw">
        {/* ========== TOP BAR (Task 3: action buttons at top) ========== */}
        <div className="pcw-top">
          <div className="pcw-top-left">
            <div className="pcw-top-logo"><i className="fas fa-cogs"></i></div>
            <h2>
              Preference Generator
              {user.branch && <span style={{fontSize:'0.65rem',color:'#38bdf8',background:'rgba(14,165,233,0.15)',padding:'3px 8px',borderRadius:'12px',marginLeft:'8px',verticalAlign:'middle'}}><i className="fas fa-building" style={{marginRight:4}}></i>{user.branch}</span>}
            </h2>
          </div>
          <div className="pcw-top-center">

            <button className="pcw-topbtn save" onClick={handleSaveDraft} disabled={saving}><i className="fas fa-save"></i>{saving?"Saving...":"Save Draft"}</button>
            {isActiveVersionFinalized() && (
              <button 
                className="pcw-topbtn" 
                onClick={handleOpenDuplicateModal}
                style={{
                  background: 'rgba(16, 185, 129, 0.12)', 
                  border: '1px solid rgba(16, 185, 129, 0.25)', 
                  color: '#34d399'
                }}
              >
                <i className="fas fa-copy" style={{marginRight:5}}></i>Duplicate Version
              </button>
            )}
            <button className="pcw-topbtn finalize" onClick={handleFinalize}><i className="fas fa-check-circle"></i>Finalize</button>
            <button className="pcw-topbtn export" onClick={handleExportCSV}><i className="fas fa-file-csv"></i>Export CSV</button>
             <button className="pcw-topbtn print" onClick={handlePrint}><i className="fas fa-print"></i>Print</button>
             <button 
               className="pcw-topbtn" 
               onClick={() => setShowCutoffPage(true)}
               style={{
                 background: 'rgba(245,158,11,0.12)', 
                 border: '1px solid rgba(245,158,11,0.25)', 
                 color: '#f59e0b'
               }}
             >
               <i className="fas fa-search" style={{marginRight:5}}></i>Check Cutoff Data
             </button>
             {activeVersionId && (
               <button 
                 className="pcw-topbtn" 
                 onClick={handleDeleteVersion} 
                 style={{
                   background: 'rgba(239,68,68,0.1)', 
                   border: '1px solid rgba(239,68,68,0.25)', 
                   color: '#fca5a5',
                   transition: 'all 0.15s'
                 }}
                 onMouseOver={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.25)'; e.currentTarget.style.color = '#fff'; }}
                 onMouseOut={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#fca5a5'; }}
               >
                 <i className="fas fa-trash-alt" style={{marginRight:5}}></i>Delete Version
               </button>
             )}
          </div>
          <div className="pcw-top-right">
            {selectedStudent && <span className="pcw-chip student"><i className="fas fa-user" style={{marginRight:3}}></i>{selectedStudent.name}</span>}
            {selectedStudent && (
              <span className="pcw-chip" style={{ background: 'rgba(167, 139, 250, 0.12)', border: '1px solid rgba(167, 139, 250, 0.3)', color: '#c084fc', fontSize: '0.72rem', fontWeight: 600 }}>
                <i className="fas fa-id-card" style={{ marginRight: 4 }}></i>
                Category: <strong>{selectedStudent.category || "OPEN"}</strong>
              </span>
            )}
            {selectedStudent && selectedStudent.special_reservation && (
              <span className="pcw-chip" style={{ background: 'rgba(236, 72, 153, 0.12)', border: '1px solid rgba(236, 72, 153, 0.3)', color: '#f472b6', fontSize: '0.72rem', fontWeight: 600 }}>
                <i className="fas fa-star" style={{ marginRight: 4 }}></i>
                Res: <strong>{selectedStudent.special_reservation}</strong>
              </span>
            )}
            <span className="pcw-chip user">{user.name}</span>
            <button className="pcw-logout" onClick={logout}>Logout</button>
          </div>
        </div>

        {/* ========== BODY ========== */}
        <div className="pcw-body">
          {showCutoffPage ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '1.5rem', background: '#0a0e1a', overflowY: 'auto' }}>
              {/* Checker Header */}
              
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-search" style={{ color: '#f59e0b' }}></i> Check 2026 R1 Cutoff Data
                </h3>
                <button 
                    onClick={() => setShowCutoffPage(false)} 
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: '#cbd5e1', padding: '0.45rem 0.9rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, transition: 'all 0.2s', fontFamily: 'inherit' }}
                    onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                    onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                  >
                    <i className="fas fa-arrow-left"></i> Back to Generator
                </button>
              </div>

              {/* Top Horizontal Filter Bar */}
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', flexShrink: 0 }}>
                
                {/* Row 1: Dropdown Selectors */}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  
                  {/* College Selector */}
                  <div style={{ flex: '2.5 1 300px', display: 'flex', flexDirection: 'column', gap: '0.4rem', position: 'relative' }} ref={collegeDropdownRef}>
                    <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8' }}>Select College(s)</label>
                    <input 
                      type="text" 
                      placeholder="Search college by name or code..." 
                      value={collegeSearchQuery}
                      onChange={e => {
                        setCollegeSearchQuery(e.target.value);
                        setShowCollegeDropdown(true);
                      }}
                      onFocus={() => setShowCollegeDropdown(true)}
                      style={{ padding: '0.5rem 0.75rem', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.85rem', outline: 'none' }}
                    />
                    {showCollegeDropdown && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, maxHeight: '200px', overflowY: 'auto', background: '#0a0e1a', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '6px', boxShadow: '0 10px 25px rgba(0,0,0,0.6)' }}>
                        {loadingCutoffColleges ? (
                          <span style={{ fontSize: '0.78rem', color: '#94a3b8', padding: '0.25rem' }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Loading colleges...</span>
                        ) : (
                          cutoffColleges
                            .filter(c => !collegeSearchQuery.trim() || c.name.toLowerCase().includes(collegeSearchQuery.toLowerCase()) || c.code.toString().includes(collegeSearchQuery))
                            .map(c => {
                              const isSelected = selectedCutoffColleges.some(sc => sc.code === c.code);
                              return (
                                <div 
                                  key={c.code} 
                                  onClick={() => {
                                    setSelectedCutoffColleges(prev => isSelected ? prev.filter(sc => sc.code !== c.code) : [...prev, c]);
                                  }}
                                  style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '6px 8px', background: isSelected ? 'rgba(14,165,233,0.08)' : 'transparent', border: isSelected ? '1px solid rgba(14,165,233,0.3)' : '1px solid transparent', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.15s' }}
                                >
                                  <div style={{ width: '15px', height: '15px', borderRadius: '4px', border: isSelected ? '1px solid #38bdf8' : '1px solid rgba(255,255,255,0.2)', background: isSelected ? '#0ea5e9' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px', flexShrink: 0 }}>
                                    {isSelected && <i className="fas fa-check" style={{ fontSize: '9px', color: '#fff' }}></i>}
                                  </div>
                                  <span style={{ fontSize: '0.8rem', color: isSelected ? '#38bdf8' : '#cbd5e1', lineHeight: '1.3' }}><strong>[{c.code}]</strong> {c.name}</span>
                                </div>
                              );
                            })
                        )}
                      </div>
                    )}
                    {selectedCutoffColleges.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.25rem' }}>
                        {selectedCutoffColleges.map(c => (
                          <span key={c.code} style={{ fontSize: '0.68rem', padding: '3px 8px', background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.25)', color: '#38bdf8', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            [{c.code}] {c.name.split(',')[0]}
                            <span onClick={(e) => { e.stopPropagation(); setSelectedCutoffColleges(prev => prev.filter(sc => sc.code !== c.code)); }} style={{ cursor: 'pointer', fontWeight: 'bold', color: '#ef4444', marginLeft: '2px' }}>&times;</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Branch Selector */}
                  <div style={{ flex: '2 1 240px', display: 'flex', flexDirection: 'column', gap: '0.4rem', position: 'relative' }} ref={branchDropdownRef}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8' }}>Select Branch(es)</label>
                      {cutoffBranches.length > 0 && (
                        <div style={{ display: 'flex', gap: '0.6rem' }}>
                          <span onClick={(e) => { e.stopPropagation(); setSelectedCutoffBranches(cutoffBranches); }} style={{ fontSize: '0.7rem', color: '#38bdf8', cursor: 'pointer', fontWeight: 600 }}>Select All</span>
                          <span onClick={(e) => { e.stopPropagation(); setSelectedCutoffBranches([]); }} style={{ fontSize: '0.7rem', color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}>Clear</span>
                        </div>
                      )}
                    </div>
                    <input 
                      type="text" 
                      placeholder="Search branch name..." 
                      value={branchSearchQuery}
                      onChange={e => {
                        setBranchSearchQuery(e.target.value);
                        setShowBranchDropdown(true);
                      }}
                      onFocus={() => setShowBranchDropdown(true)}
                      style={{ padding: '0.5rem 0.75rem', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.85rem', outline: 'none' }}
                    />
                    {showBranchDropdown && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, maxHeight: '200px', overflowY: 'auto', background: '#0a0e1a', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '6px', boxShadow: '0 10px 25px rgba(0,0,0,0.6)' }}>
                        {loadingCutoffBranches ? (
                          <span style={{ fontSize: '0.78rem', color: '#94a3b8', padding: '0.25rem' }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Loading branches...</span>
                        ) : cutoffBranches.length === 0 ? (
                          <span style={{ fontSize: '0.75rem', color: '#64748b', padding: '0.25rem' }}>Select college(s) first to load branches</span>
                        ) : (
                          cutoffBranches
                            .filter(b => !branchSearchQuery.trim() || b.toLowerCase().includes(branchSearchQuery.toLowerCase()))
                            .map(b => {
                              const isSelected = selectedCutoffBranches.includes(b);
                              return (
                                <div 
                                  key={b} 
                                  onClick={() => {
                                    setSelectedCutoffBranches(prev => isSelected ? prev.filter(x => x !== b) : [...prev, b]);
                                  }}
                                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 8px', background: isSelected ? 'rgba(14,165,233,0.08)' : 'transparent', border: isSelected ? '1px solid rgba(14,165,233,0.3)' : '1px solid transparent', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.15s' }}
                                >
                                  <div style={{ width: '15px', height: '15px', borderRadius: '4px', border: isSelected ? '1px solid #38bdf8' : '1px solid rgba(255,255,255,0.2)', background: isSelected ? '#0ea5e9' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {isSelected && <i className="fas fa-check" style={{ fontSize: '9px', color: '#fff' }}></i>}
                                  </div>
                                  <span style={{ fontSize: '0.8rem', color: isSelected ? '#38bdf8' : '#cbd5e1' }}>{b}</span>
                                </div>
                              );
                            })
                        )}
                      </div>
                    )}
                    {selectedCutoffBranches.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.25rem' }}>
                        {selectedCutoffBranches.map(b => (
                          <span key={b} style={{ fontSize: '0.68rem', padding: '3px 8px', background: 'rgba(14,165,233,0.12)', border: '1px solid rgba(14,165,233,0.25)', color: '#38bdf8', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            {b}
                            <span onClick={(e) => { e.stopPropagation(); setSelectedCutoffBranches(prev => prev.filter(x => x !== b)); }} style={{ cursor: 'pointer', fontWeight: 'bold', color: '#ef4444', marginLeft: '2px' }}>&times;</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Student Category */}
                  <div style={{ flex: '1 1 120px', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8' }}>Category</label>
                    <select 
                      value={cutoffCategory} 
                      onChange={e => setCutoffCategory(e.target.value)}
                      className="pcw-select-input"
                      style={{ height: '38px', padding: '0.35rem 0.65rem' }}
                    >
                      <option value="OPEN">OPEN / General</option>
                      <option value="OBC">OBC (Other Backward Class)</option>
                      <option value="SC">SC (Scheduled Caste)</option>
                      <option value="ST">ST (Scheduled Tribe)</option>
                      <option value="VJ">VJ / DT (Vimukta Jati)</option>
                      <option value="NT1">NT-B / NT-1</option>
                      <option value="NT2">NT-C / NT-2</option>
                      <option value="NT3">NT-D / NT-3</option>
                      <option value="SBC">SBC (Special Backward Class)</option>
                    </select>
                  </div>

                  {/* Home University */}
                  <div style={{ flex: '1 1 140px', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8' }}>Home University</label>
                    <select 
                      value={cutoffHomeUniversity} 
                      onChange={e => setCutoffHomeUniversity(e.target.value)}
                      className="pcw-select-input"
                      style={{ height: '38px', padding: '0.35rem 0.65rem' }}
                    >
                      <option value="">OHU (Other than HU)</option>
                      <option value="DBAMU">DBAMU (Aurangabad)</option>
                      <option value="MU">MU (Mumbai)</option>
                      <option value="NMU">NMU (Jalgaon)</option>
                      <option value="SPPU">SPPU (Pune)</option>
                      <option value="SGBAU">SGBAU (Amravati)</option>
                      <option value="SUK">SUK (Kolhapur)</option>
                      <option value="SRTMU">SRTMU (Nanded)</option>
                      <option value="RTMNU">RTMNU (Nagpur)</option>
                      <option value="PAHSUS">PAHSUS (Solapur)</option>
                    </select>
                  </div>

                  {/* Gender Quota */}
                  <div style={{ flex: '1 1 140px', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8' }}>Gender Quota</label>
                    <select 
                      value={cutoffGender} 
                      onChange={e => setCutoffGender(e.target.value)}
                      className="pcw-select-input"
                      style={{ height: '38px', padding: '0.35rem 0.65rem' }}
                    >
                      <option value="General">General / Male</option>
                      <option value="Ladies">Female Only</option>
                    </select>
                  </div>

                </div>

                {/* Row 2: Special Quotas, Toggle, Live Category Preview & Search Button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.2rem', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.85rem' }}>
                  
                  {/* Left Group: Special Quotas and Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.8rem', flexWrap: 'wrap' }}>
                    
                    {/* Special Quotas pills */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                      <label style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.5px' }}>Special Quotas</label>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        {['TFWS', 'Defence', 'PWD', 'Orphan'].map(q => {
                          const active = cutoffSpecialQuotas.includes(q.toUpperCase());
                          return (
                            <button
                              key={q}
                              onClick={() => {
                                setCutoffSpecialQuotas(prev => 
                                  active ? prev.filter(x => x !== q.toUpperCase()) : [...prev, q.toUpperCase()]
                                );
                              }}
                              style={{
                                padding: '0.35rem 0.75rem',
                                borderRadius: '6px',
                                border: active ? '1px solid #c084fc' : '1px solid rgba(255,255,255,0.1)',
                                background: active ? 'rgba(168,85,247,0.15)' : 'transparent',
                                color: active ? '#c084fc' : '#94a3b8',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'all 0.15s'
                              }}
                            >
                              {active ? '✓ ' : ''}{q}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Toggle auto-include OPEN seats */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                      <label style={{ display: 'inline-flex', position: 'relative', width: '38px', height: '20px', cursor: 'pointer', userSelect: 'none' }}>
                        <input 
                          type="checkbox" 
                          checked={cutoffIncludeOpen} 
                          onChange={e => setCutoffIncludeOpen(e.target.checked)}
                          style={{ opacity: 0, width: 0, height: 0 }} 
                        />
                        <span style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '20px', background: cutoffIncludeOpen ? '#10b981' : '#475569', transition: 'all 0.2s' }}>
                          <span style={{ position: 'absolute', content: '""', height: '14px', width: '14px', left: cutoffIncludeOpen ? '20px' : '3px', bottom: '3px', background: '#fff', borderRadius: '50%', transition: 'all 0.2s' }}></span>
                        </span>
                      </label>
                      <span style={{ fontSize: '0.75rem', color: '#cbd5e1', fontWeight: 600 }}>Auto-include OPEN category seats</span>
                    </div>

                  </div>

                  {/* Center Group: Live Category Preview */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', minWidth: '220px' }}>
                    <label style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.5px' }}>Live Category Preview</label>
                    {(() => {
                      const base = cutoffCategory || "OPEN";
                      const isOpen = base === "OPEN";
                      const isEws = base === "EWS";
                      const prefixes = cutoffGender === "General" ? ["G"] : ["G", "L"];
                      const suffixes = cutoffHomeUniversity ? ["H", "S"] : ["O", "S"];
                      let groups = [];
                      if (isEws) {
                        groups.push({ label: "EWS", codes: ["EWS"], color: "#a78bfa" });
                      } else if (!isOpen) {
                        const baseCodes = [];
                        for (const p of prefixes) for (const s of suffixes) baseCodes.push(`${p}${base}${s}`);
                        groups.push({ label: base, codes: baseCodes, color: "#a78bfa" });
                      }
                      if (!isOpen && cutoffIncludeOpen) {
                        const openCodes = [];
                        for (const p of prefixes) for (const s of suffixes) openCodes.push(`${p}OPEN${s}`);
                        groups.push({ label: "OPEN", codes: openCodes, color: "#34d399" });
                      } else if (isOpen) {
                        const openCodes = [];
                        for (const p of prefixes) for (const s of suffixes) openCodes.push(`${p}OPEN${s}`);
                        groups.push({ label: "OPEN", codes: openCodes, color: "#a78bfa" });
                      }
                      if (cutoffSpecialQuotas.includes("TFWS")) groups.push({ label: "TFWS", codes: ["TFWS"], color: "#fbbf24" });
                      if (cutoffSpecialQuotas.includes("EWS") && !isEws) groups.push({ label: "EWS", codes: ["EWS"], color: "#fbbf24" });
                      if (cutoffSpecialQuotas.includes("DEFENCE")) {
                        const defCodes = isOpen ? ["DEFOPENS"] : [`DEF${base}S`, "DEFOPENS"];
                        groups.push({ label: "Defence", codes: defCodes, color: "#60a5fa" });
                      }
                      if (cutoffSpecialQuotas.includes("PWD")) {
                        const pwdCodes = isOpen ? ["PWDOPENS"] : [`PWD${base}S`, "PWDOPENS"];
                        groups.push({ label: "PWD", codes: pwdCodes, color: "#60a5fa" });
                      }
                      if (cutoffSpecialQuotas.includes("ORPHAN")) groups.push({ label: "Orphan", codes: ["ORPHAN"], color: "#f472b6" });

                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.8rem', alignItems: 'center' }}>
                          {groups.map(g => (
                            <div key={g.label} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                              <span style={{ fontSize: '0.62rem', fontWeight: 700, color: g.color, textTransform: 'uppercase' }}>{g.label}:</span>
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                {g.codes.map(code => (
                                  <span key={code} style={{ fontSize: '0.62rem', padding: '2px 5px', borderRadius: '4px', background: `${g.color}15`, border: `1px solid ${g.color}30`, color: g.color, fontFamily: 'monospace', fontWeight: 600 }}>{code}</span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Right Group: Search Button */}
                  <button 
                    onClick={handleSearchCutoffs} 
                    disabled={searchingCutoffs}
                    style={{ padding: '0.65rem 1.8rem', borderRadius: '8px', background: '#f59e0b', color: '#000', fontSize: '0.88rem', fontWeight: 'bold', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.15s' }}
                  >
                    {searchingCutoffs ? (
                      <><i className="fas fa-spinner fa-spin"></i> Searching...</>
                    ) : (
                      <><i className="fas fa-search"></i> Search Cutoffs</>
                    )}
                  </button>

                </div>

              </div>

              {/* Stacked Lower Section: Search Results */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.85rem', color: '#cbd5e1', fontWeight: 600 }}>Results: {cutoffResults.length} records found</span>
                  {cutoffResults.length > 0 && (
                    <span onClick={() => setCutoffResults([])} style={{ fontSize: '0.8rem', color: '#ef4444', cursor: 'pointer', fontWeight: 600 }}>Clear Results</span>
                  )}
                </div>

                <div style={{ flex: 1, overflowY: 'auto', background: 'rgba(0,0,0,0.3)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  {cutoffResults.length === 0 ? (
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#475569', gap: '12px', padding: '3rem' }}>
                      <i className="fas fa-search-minus" style={{ fontSize: '2.5rem' }}></i>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Select colleges/filters and click Search</span>
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', color: '#cbd5e1', textAlign: 'left' }}>
                      <thead style={{ background: 'rgba(255,255,255,0.03)', position: 'sticky', top: 0, zIndex: 1, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <tr>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>Inst. Code</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>College / Institute</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>Branch Name</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>Category</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#f59e0b' }}>R1 Rank</th>
                          <th style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#38bdf8' }}>R1 Percentile</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cutoffResults.map((r, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                            <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace' }}>{r["Institute Code"] || r["Institute CODE"]}</td>
                            <td style={{ padding: '0.75rem 1rem' }} title={r["INSTITUTE"]}>{r["INSTITUTE"]}</td>
                            <td style={{ padding: '0.75rem 1rem' }}>{r["BRANCH NAME"]}</td>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 600 }}>{r["CATEGORY"]}</td>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 'bold', color: '#f59e0b' }}>{r["R1 RANK"] !== null ? r["R1 RANK"].toLocaleString() : '--'}</td>
                            <td style={{ padding: '0.75rem 1rem', color: '#38bdf8' }}>{r["R1 PERCENTILE"] !== null ? r["R1 PERCENTILE"].toFixed(7) : '--'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
            ) : (
            <React.Fragment>
              {/* Task 2: Sidebar toggle */}
              <div className={`pcw-sb-toggle ${sidebarOpen ? 'shifted' : ''}`} onClick={() => setSidebarOpen(!sidebarOpen)}>
                <i className={`fas fa-chevron-${sidebarOpen ? 'left' : 'right'}`}></i>
              </div>

          {/* Collapsible sidebar */}
          <div className={`pcw-sidebar ${sidebarOpen ? '' : 'collapsed'}`}>
            <div className="pcw-sb-head">
              <h4><i className="fas fa-users" style={{marginRight:5}}></i>Students</h4>
              <input className="pcw-sb-search" placeholder="Search..." value={studentSearch} onChange={e => setStudentSearch(e.target.value)} />
              <button className="pcw-sb-add" onClick={() => setShowStudentModal(true)}>
                <i className="fas fa-plus" style={{marginRight:3}}></i>New Student
              </button>
            </div>
            <div className="pcw-sb-list">
              {students.map(s => (
                <div key={s.id} className={`pcw-sb-item ${selectedStudent?.id === s.id ? 'active' : ''}`}
                  onClick={() => { setSelectedStudent(s); setActiveVersionId(null); loadStudentState(s.id, examTab); setTimeout(() => mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' }), 100); }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="pcw-sb-item-name">{s.name}</div>
                    <div className="pcw-sb-item-actions" style={{ display: 'flex', gap: '8px' }}>
                      <i className="fas fa-edit" style={{ fontSize: '0.8rem', cursor: 'pointer', color: '#94a3b8', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = '#38bdf8'} onMouseOut={e => e.currentTarget.style.color = '#94a3b8'} onClick={(e) => handleOpenEditStudent(e, s)} title="Edit Student"></i>
                      <i className="fas fa-trash-alt" style={{ fontSize: '0.8rem', cursor: 'pointer', color: '#94a3b8', transition: 'color 0.2s' }} onMouseOver={e => e.currentTarget.style.color = '#ef4444'} onMouseOut={e => e.currentTarget.style.color = '#94a3b8'} onClick={(e) => handleDeleteStudent(e, s)} title="Delete Student"></i>
                    </div>
                  </div>
                  <div className="pcw-sb-item-reg" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', marginTop: '3px' }}>
                    <span>{s.registration_number}</span>
                    <span style={{ fontSize: '0.62rem', background: 'rgba(167, 139, 250, 0.15)', color: '#c084fc', padding: '1px 4px', borderRadius: '4px' }}>{s.category || "OPEN"}</span>
                    {s.special_reservation && (
                      <span style={{ fontSize: '0.62rem', background: 'rgba(236, 72, 153, 0.15)', color: '#f472b6', padding: '1px 4px', borderRadius: '4px' }}>{s.special_reservation}</span>
                    )}
                  </div>
                </div>
              ))}
              {students.length === 0 && <div className="pcw-empty">No students</div>}
            </div>
          </div>

          {/* Main workspace — full width (Task 4) */}
          <div className="pcw-main" ref={mainRef}>
            {!selectedStudent ? (
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',flex:1,color:'#475569',flexDirection:'column',gap:'0.75rem'}}>
                <i className="fas fa-hand-pointer" style={{fontSize:'2rem'}}></i>
                <p style={{fontSize:'0.95rem',fontWeight:600}}>Select a student from the sidebar to begin</p>
              </div>
            ) : (
              <>
                {/* Exam tabs — selections persist across tabs (Task 1) */}
                <div className="pcw-exam-tabs">
                  <button className={`pcw-exam-tab ${examTab==='CET' && !activeVersionId ?'active':''}`} onClick={()=>{setExamTab('CET'); setActiveVersionId(null); loadStudentState(selectedStudent.id, 'CET');}}>
                    <i className="fas fa-map-marked-alt" style={{marginRight:5}}></i>CET
                  </button>
                  <button className={`pcw-exam-tab ${examTab==='JEE' && !activeVersionId ?'active':''}`} onClick={()=>{setExamTab('JEE'); setActiveVersionId(null); loadStudentState(selectedStudent.id, 'JEE');}}>
                    <i className="fas fa-globe" style={{marginRight:5}}></i>JEE
                  </button>
                  {studentVersions.map(v => (
                    <button 
                      key={v.id} 
                      className={`pcw-exam-tab ${activeVersionId===v.id ? 'active' : ''}`} 
                      onClick={() => handleSelectVersion(v)}
                    >
                      <i className={v.status === 'FINALIZED' ? "fas fa-lock" : "fas fa-file-alt"} style={{marginRight:5, color: v.status === 'FINALIZED' ? '#fbbf24' : '#38bdf8'}}></i>
                      {v.list_type} ({v.version_label || `V${v.version || 1}`})
                    </button>
                  ))}
                </div>

                {/* Filters & Summary */}
                {!showFilters ? (
                  <div className="pcw-summary">
                    <div className="pcw-summary-left">
                      {percentile && <span className="pcw-summary-item"><i className="fas fa-percentage"></i> Percentile: <strong>{percentile}</strong> <span style={{fontSize:'0.68rem',opacity:0.7}}>[{(parseFloat(percentile)-(parseFloat(percMinusBuf)||0)).toFixed(1)} – {(parseFloat(percPlusBuf) === 0 || !percPlusBuf || parseFloat(percPlusBuf) === 0.0) ? "100.0" : (parseFloat(percentile)+(parseFloat(percPlusBuf)||0)).toFixed(1)}]</span></span>}
                      {rank && <span className="pcw-summary-item"><i className="fas fa-hashtag"></i> Rank: <strong>{rank}</strong> <span style={{fontSize:'0.68rem',opacity:0.7}}>[{Math.max(1,parseFloat(rank)-(parseFloat(rankMinusBuf)||0)).toLocaleString()} – {(parseFloat(rank)+(parseFloat(rankPlusBuf)||0)).toLocaleString()}]</span></span>}
                      <span className="pcw-summary-item"><i className="fas fa-city"></i> Cities: <strong>{selectedCities.length || 'All'}</strong></span>
                      <span className="pcw-summary-item"><i className="fas fa-book"></i> Courses: <strong>{selectedCourseGroups.length || 'All'}</strong></span>
                      {examTab === 'CET' && <span className="pcw-summary-item"><i className="fas fa-tags"></i> Category: <strong>{baseCategory || 'OPEN'}</strong>{specialQuotas.length > 0 ? ` + ${specialQuotas.join(', ')}` : ''}</span>}
                      {examTab === 'CET' && homeUniversity && <span className="pcw-summary-item" style={{color:'#a78bfa',borderColor:'rgba(167,139,250,0.3)'}}><i className="fas fa-university"></i> HU: <strong>{homeUniversity}</strong></span>}
                      {examTab === 'CET' && <span className="pcw-summary-item" style={{color:'#fb923c',borderColor:'rgba(251,146,60,0.3)'}}><i className="fas fa-venus-mars"></i> <strong>{gender || 'General'}</strong></span>}
                      {enablePriority && <span className="pcw-summary-item" style={{color:'#fbbf24',borderColor:'rgba(245,158,11,0.3)'}}><i className="fas fa-sort-numeric-down"></i> Priority ON</span>}
                    </div>
                    {user?.role === 'ADMIN' && (
                      <button 
                        className="pcw-edit-filters-btn" 
                        onClick={async () => {
                          try {
                            toast.info("Healing old drafts... please wait");
                            const res = await api.get('/preferences/trigger-draft-patch');
                            toast.success(`Successfully healed ${res.data.drafts_updated} old drafts! Please refresh the page.`);
                          } catch (e) {
                            toast.error("Failed to heal drafts.");
                          }
                        }}
                        style={{ background: 'rgba(234, 179, 8, 0.1)', color: '#facc15', borderColor: 'rgba(234, 179, 8, 0.2)' }}
                        title="Inject Branch Ranks into old saved drafts"
                      >
                        <i className="fas fa-magic" style={{marginRight:5}}></i>Heal Drafts
                      </button>
                    )}
                    <button className="pcw-edit-filters-btn" onClick={() => setShowFilters(true)}>
                      <i className="fas fa-sliders-h" style={{marginRight:5}}></i>Edit Filters
                    </button>
                  </div>
                ) : (
                  <div className="pcw-filters">
                    <div className="pcw-premium-filters-grid">
                      {/* CARD 1: ACADEMIC SCORES */}
                      <div className="pcw-premium-card">
                        <div className="pcw-premium-card-title"><i className="fas fa-chart-line"></i> Academic Scores & Buffers</div>
                        
                        <div className="pcw-score-group">
                          <div className="pcw-score-header">
                            <span className="pcw-score-label">Percentile</span>
                            <div className="pcw-buffers-container">
                              <div className="pcw-buffer-item" title="Safe: include colleges this many points below your percentile">
                                <i className="fas fa-minus-circle" style={{color: '#34d399'}}></i>
                                <input className="hide-spinners" type="number" step="0.1" min="0" value={percMinusBuf} onChange={e=>setPercMinusBuf(e.target.value)} placeholder="5" />
                              </div>
                              <div className="pcw-buffer-item" title="Dream: include colleges this many points above your percentile">
                                <i className="fas fa-plus-circle" style={{color: '#c084fc'}}></i>
                                <input className="hide-spinners" type="number" step="0.1" min="0" value={percPlusBuf} onChange={e=>setPercPlusBuf(e.target.value)} placeholder="0" />
                              </div>
                            </div>
                          </div>
                          <input 
                            className="pcw-perc-input pcw-score-main-input hide-spinners" 
                            type="number" 
                            step="0.01" 
                            value={percentile} 
                            onChange={e=>setPercentile(e.target.value)} 
                            placeholder={!rank ? "Required if no Rank" : "Optional"} 
                            style={{ borderColor: (!percentile && !rank) ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)' }}
                          />
                        </div>

                        <div className="pcw-score-group">
                          <div className="pcw-score-header">
                            <span className="pcw-score-label">Rank / Merit</span>
                            <div className="pcw-buffers-container">
                              <div className="pcw-buffer-item" title="Dream: include colleges with better (lower) rank by this amount">
                                <i className="fas fa-minus-circle" style={{color: '#c084fc'}}></i>
                                <input className="hide-spinners" type="number" min="0" value={rankMinusBuf} onChange={e=>setRankMinusBuf(e.target.value)} placeholder="0" />
                              </div>
                              <div className="pcw-buffer-item" title="Safe: include colleges with worse (higher) rank by this amount">
                                <i className="fas fa-plus-circle" style={{color: '#34d399'}}></i>
                                <input className="hide-spinners" type="number" min="0" value={rankPlusBuf} onChange={e=>setRankPlusBuf(e.target.value)} placeholder="5000" />
                              </div>
                            </div>
                          </div>
                          <input 
                            className="pcw-perc-input pcw-score-main-input hide-spinners" 
                            type="number" 
                            value={rank} 
                            onChange={e=>handleRankChange(e.target.value)} 
                            placeholder={!percentile ? "Required if no Perc" : "Optional"} 
                            style={{ borderColor: (!percentile && !rank) ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)' }}
                          />
                        </div>
                      </div>

                      {/* CARD 2: PREDICTORS (CET ONLY) */}
                      {examTab === 'CET' && showRankPredictor && percentile && getPredictedRanks() && (
                        <div className="pcw-premium-card">
                          <div className="pcw-premium-card-title"><i className="fas fa-magic" style={{color:'#f59e0b'}}></i> Rank Predictor</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
                            {/* Option 1 */}
                            {getPredictedRanks().predicted1 !== null && (
                              <div className="pcw-predictor-card" title="Matches nearest student percentile from previous year" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '0.75rem', position: 'relative' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#c084fc' }}>Option 1: Historical Data Match</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Buffer:</span>
                                    <input className="hide-spinners" type="number" value={opt1BufferPercent} onChange={e => setOpt1BufferPercent(e.target.value)} onClick={e => e.stopPropagation()} style={{ width: '55px', background: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '4px', color: '#fff', fontSize: '0.7rem', padding: '2px 4px', textAlign: 'center', outline: 'none' }} />
                                    <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>%</span>
                                  </div>
                                </div>
                                <div style={{ fontSize: '0.65rem', color: '#94a3b8', lineHeight: '1.3', marginTop: '4px' }}>
                                  Nearest 2025 SML: <strong>{getPredictedRanks().nearest1.sml.toLocaleString()}</strong> (@ {getPredictedRanks().nearest1.perc}%)
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#f1f5f9' }}>~ {getPredictedRanks().predicted1.toLocaleString()}</div>
                                  <button onClick={() => { handleRankChange(getPredictedRanks().predicted1.toString()); toast.success(`Applied rank: ${getPredictedRanks().predicted1.toLocaleString()}`); }} style={{ background: 'rgba(192, 132, 252, 0.12)', border: '1px solid rgba(192, 132, 252, 0.25)', borderRadius: '5px', color: '#c084fc', padding: '3px 7px', fontSize: '0.68rem', fontWeight: '600', cursor: 'pointer' }}>Apply Rank</button>
                                </div>
                              </div>
                            )}
                            
                            {/* Option 3 */}
                            {getPredictedRanks().predicted3 !== null && (
                              <div className="pcw-predictor-card" title="Equivalent Percentile Match" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '0.75rem', position: 'relative' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#f59e0b' }}>Option 3: Eqv. Percentile Match</span>
                                </div>
                                <div style={{ fontSize: '0.65rem', color: '#94a3b8', lineHeight: '1.3', marginTop: '4px' }}>
                                  Eqv. Perc: <strong>{getPredictedRanks().equivPerc.toFixed(4)}</strong>%<br/>
                                  Nearest SML: <strong>{getPredictedRanks().nearest3.sml.toLocaleString()}</strong> (@ {getPredictedRanks().nearest3.perc}%)
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#f1f5f9' }}>~ {getPredictedRanks().predicted3.toLocaleString()}</div>
                                  <button onClick={() => { handleRankChange(getPredictedRanks().predicted3.toString()); toast.success(`Applied rank: ${getPredictedRanks().predicted3.toLocaleString()}`); }} style={{ background: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.25)', borderRadius: '5px', color: '#f59e0b', padding: '3px 7px', fontSize: '0.68rem', fontWeight: '600', cursor: 'pointer' }}>Apply Rank</button>
                                </div>
                              </div>
                            )}

                            {/* Option 2 */}
                            <div className="pcw-predictor-card" title="Direct Formula Estimation" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', padding: '0.75rem', position: 'relative' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#34d399' }}>Option 2: Direct Formula</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Buffer:</span>
                                  <input className="hide-spinners" type="number" value={opt2BufferPercent} onChange={e => setOpt2BufferPercent(e.target.value)} onClick={e => e.stopPropagation()} style={{ width: '55px', background: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '4px', color: '#fff', fontSize: '0.7rem', padding: '2px 4px', textAlign: 'center', outline: 'none' }} />
                                  <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>%</span>
                                </div>
                              </div>
                              <div style={{ fontSize: '0.65rem', color: '#94a3b8', lineHeight: '1.3', marginTop: '4px' }}>
                                Formula: (100 - {percentile}) x 4,54,069 / 100
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                                <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#f1f5f9' }}>~ {getPredictedRanks().predicted2.toLocaleString()}</div>
                                <button onClick={() => { handleRankChange(getPredictedRanks().predicted2.toString()); toast.success(`Applied rank: ${getPredictedRanks().predicted2.toLocaleString()}`); }} style={{ background: 'rgba(52, 211, 153, 0.12)', border: '1px solid rgba(52, 211, 153, 0.25)', borderRadius: '5px', color: '#34d399', padding: '3px 7px', fontSize: '0.68rem', fontWeight: '600', cursor: 'pointer' }}>Apply Rank</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* CARD 3: PREFERENCES */}
                      <div className="pcw-premium-card" style={{ gridColumn: (examTab === 'CET' && showRankPredictor && percentile && getPredictedRanks()) ? 'span 1' : 'span 2' }}>
                        <div className="pcw-premium-card-title"><i className="fas fa-map-marker-alt"></i> Preferences</div>
                        <SearchableMultiSelect 
                          label={`City (Shared) ${selectedCities.length > 0 ? `· ${selectedCities.length} selected` : ''}`}
                          options={filterOptions.cities}
                          selected={selectedCities}
                          setSelected={setSelectedCities}
                          enablePriority={enablePriority}
                          setPriorityVal={setPriorityVal}
                        />
                        <SearchableMultiSelect 
                          label={`Course Group (Shared) ${selectedCourseGroups.length > 0 ? `· ${selectedCourseGroups.length} selected` : ''}`}
                          options={filterOptions.course_groups}
                          selected={selectedCourseGroups}
                          setSelected={setSelectedCourseGroups}
                          enablePriority={enablePriority}
                          setPriorityVal={setPriorityVal}
                        />
                      </div>

                      {/* CARD 4: DEMOGRAPHICS */}
                      <div className="pcw-premium-card">
                        <div className="pcw-premium-card-title"><i className="fas fa-users"></i> Demographics</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', alignItems: 'stretch' }}>
                          
                          {examTab === "CET" && (() => {
                            const computePreview = () => {
                              const base = baseCategory || "OPEN";
                              const isOpen = base === "OPEN";
                              const isEws = base === "EWS";
                              const isTfwsBase = base === "TFWS";
                              const isMale = gender === "General";
                              const hasHomeUni = !!homeUniversity;
                              const prefixes = isMale ? ["G"] : ["G", "L"];
                              const suffixes = hasHomeUni ? ["H", "S"] : ["O", "S"];
                              let groups = [];
                              if (isEws) {
                                groups.push({ label: "EWS Seats", codes: ["EWS"], color: "#a78bfa" });
                              } else if (!isOpen && !isTfwsBase) {
                                const baseCodes = [];
                                for (const p of prefixes) for (const s of suffixes) baseCodes.push(`${p}${base}${s}`);
                                groups.push({ label: `${base} Seats`, codes: baseCodes, color: "#a78bfa" });
                              }
                              if (!isOpen) {
                                if (includeOpenCategory) {
                                  const openCodes = [];
                                  for (const p of prefixes) for (const s of suffixes) openCodes.push(`${p}OPEN${s}`);
                                  groups.push({ label: "OPEN (auto-included)", codes: openCodes, color: "#34d399" });
                                }
                              } else {
                                const openCodes = [];
                                for (const p of prefixes) for (const s of suffixes) openCodes.push(`${p}OPEN${s}`);
                                groups.push({ label: "OPEN Seats", codes: openCodes, color: "#a78bfa" });
                              }
                              if (specialQuotas.includes("TFWS")) groups.push({ label: "TFWS", codes: ["TFWS"], color: "#fbbf24" });
                              if (specialQuotas.includes("EWS")) groups.push({ label: "EWS", codes: ["EWS"], color: "#fbbf24" });
                              if (specialQuotas.includes("DEFENCE")) {
                                const defCodes = isOpen ? ["DEFOPENS"] : [`DEF${base}S`, "DEFOPENS"];
                                groups.push({ label: "Defence", codes: defCodes, color: "#60a5fa" });
                              }
                              if (specialQuotas.includes("PWD")) {
                                const pwdCodes = isOpen ? ["PWDOPENS"] : [`PWD${base}S`, "PWDOPENS"];
                                groups.push({ label: "PWD", codes: pwdCodes, color: "#60a5fa" });
                              }
                              if (specialQuotas.includes("ORPHAN")) groups.push({ label: "Orphan", codes: ["ORPHAN"], color: "#f472b6" });
                              return groups;
                            };
                            const preview = computePreview();
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '1rem', background: 'rgba(167,139,250,0.05)', borderRadius: '10px', border: '1px solid rgba(167,139,250,0.2)' }}>
                                <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8' }}>
                                  Student Category (CET Only)
                                </label>
                                <select className="pcw-select-input" value={baseCategory} onChange={e => setBaseCategory(e.target.value)} id="base-category-select" style={{background:'rgba(0,0,0,0.4)', padding:'0.5rem', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.1)', color:'#fff', outline:'none', fontSize:'0.85rem'}}>
                                  <option value="OPEN">OPEN (General)</option>
                                  <option value="OBC">OBC</option>
                                  <option value="SC">SC</option>
                                  <option value="ST">ST</option>
                                  <option value="VJ">VJ / DT</option>
                                  <option value="NT1">NT1 (B)</option>
                                  <option value="NT2">NT2 (C)</option>
                                  <option value="NT3">NT3 (D)</option>
                                  <option value="SEBC">SEBC</option>
                                  <option value="EWS">EWS</option>
                                </select>
                                <div>
                                  <label style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', marginBottom: '0.4rem', display: 'block' }}>Special Quotas</label>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    {[ { key: 'TFWS', label: 'TFWS' }, { key: 'DEFENCE', label: 'Defence' }, { key: 'PWD', label: 'PWD' }, { key: 'ORPHAN', label: 'Orphan' } ].map(q => {
                                      const active = specialQuotas.includes(q.key);
                                      return (
                                        <button key={q.key} onClick={() => setSpecialQuotas(prev => active ? prev.filter(x => x !== q.key) : [...prev, q.key])} style={{ padding: '0.25rem 0.65rem', borderRadius: '6px', border: active ? '1px solid #a78bfa' : '1px solid rgba(255,255,255,0.1)', background: active ? 'rgba(167,139,250,0.2)' : 'rgba(255,255,255,0.04)', color: active ? '#c084fc' : '#94a3b8', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                                          {active ? '✓ ' : ''}{q.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div style={{ marginTop: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <label style={{ position: 'relative', display: 'inline-block', width: '32px', height: '18px' }}>
                                    <input type="checkbox" checked={includeOpenCategory} onChange={(e) => setIncludeOpenCategory(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                                    <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: includeOpenCategory ? '#34d399' : 'rgba(255,255,255,0.1)', transition: '.4s', borderRadius: '34px' }}>
                                      <span style={{ position: 'absolute', content: '""', height: '14px', width: '14px', left: includeOpenCategory ? '16px' : '2px', bottom: '2px', backgroundColor: 'white', transition: '.4s', borderRadius: '50%' }}></span>
                                    </span>
                                  </label>
                                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8' }}>Auto-include OPEN category seats</span>
                                </div>
                                <div style={{ borderTop: '1px solid rgba(167,139,250,0.15)', paddingTop: '0.6rem', marginTop: '0.5rem' }}>
                                  <label style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#64748b', marginBottom: '0.5rem', display: 'block' }}>
                                    <i className="fas fa-search" style={{ marginRight: 4 }}></i> Live Search Preview:
                                  </label>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                    {preview.map((group, gi) => (
                                      <div key={gi} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                                        <span style={{ fontSize: '0.6rem', fontWeight: 700, color: group.color, minWidth: '90px', paddingTop: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{group.label}</span>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                          {group.codes.map(code => (
                                            <span key={code} style={{ fontSize: '0.6rem', padding: '1px 6px', borderRadius: '4px', background: `${group.color}18`, border: `1px solid ${group.color}40`, color: group.color, fontWeight: 600, fontFamily: 'monospace' }}>{code}</span>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                        </div>
                      </div>
                    </div>

                    {/* NEW ROW: HORIZONTAL GENDER & HOME UNIVERSITY & ADMISSION ZONE SETTINGS */}
                    <div className="pcw-premium-card" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '1.5rem', width: '100%', marginBottom: '1.5rem', alignItems: 'flex-end', overflow: 'visible' }}>
                      {examTab === "CET" && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flex: '1 1 200px' }}>
                          <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8' }}>
                            <i className="fas fa-university" style={{ marginRight: 5, color: '#a78bfa' }}></i>Home University
                          </label>
                          <select className="pcw-select-input" value={homeUniversity} onChange={e => setHomeUniversity(e.target.value)} id="home-university-select" style={{background:'rgba(0,0,0,0.4)', padding:'0.6rem', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.1)', color:'#fff', outline:'none', fontSize:'0.85rem'}}>
                            <option value="">-- No Preference (show all) --</option>
                            <option value="MU">Mumbai University (MU)</option>
                            <option value="SPPU">Savitribai Phule Pune University (SPPU)</option>
                            <option value="SUK">Shivaji University Kolhapur (SUK)</option>
                            <option value="RTMNU">Nagpur University (RTMNU)</option>
                            <option value="SGBAU">Amravati University (SGBAU)</option>
                            <option value="SRTMUN">Marathwada University Nanded (SRTMUN)</option>
                            <option value="BAMU">Dr. BAMU Aurangabad (BAMU)</option>
                            <option value="NMU">North Maharashtra University (NMU)</option>
                            <option value="PAHSU">Solapur University (PAHSU)</option>
                            <option value="GUG">Gondwana University (GUG)</option>
                          </select>
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flex: '1 1 150px' }}>
                        <label style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8' }}>
                          <i className="fas fa-venus-mars" style={{ marginRight: 5, color: '#fb923c' }}></i>Gender Quota
                        </label>
                        <select className="pcw-select-input" value={gender} onChange={e => setGender(e.target.value)} id="gender-quota-select" style={{background:'rgba(0,0,0,0.4)', padding:'0.6rem', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.1)', color:'#fff', outline:'none', fontSize:'0.85rem'}}>
                          <option value="General">General / Male</option>
                          <option value="Female">Female / Ladies</option>
                        </select>
                      </div>

                      {/* Admission Zone Settings */}
                      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '1rem', flex: '2 1 380px', background: 'rgba(255,255,255,0.02)', padding: '0.65rem 0.85rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: '1 1 120px' }}>
                          <label style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8' }}>
                            <i className="fas fa-sliders-h" style={{ marginRight: 5, color: '#38bdf8' }}></i>Zone Basis
                          </label>
                          <select className="pcw-select-input" value={zoneBasis} onChange={e => setZoneBasis(e.target.value)} style={{background:'rgba(0,0,0,0.3)', padding:'0.4rem 0.6rem', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.08)', color:'#fff', height:'34px', fontSize:'0.78rem'}}>
                            <option value="rank">Rank</option>
                            <option value="percentile">Percentile</option>
                          </select>
                        </div>

                        {/* Rank buffers */}
                        {zoneBasis === 'rank' && (
                          <div style={{ display: 'flex', gap: '0.5rem', flex: '1 1 180px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1 }}>
                              <label style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' }} title="Target Upper Buffer (better ranks). Ranks better than Student Rank - Buffer are Dream.">
                                Target Upper (Rank)
                              </label>
                              <input 
                                className="hide-spinners" 
                                type="number" 
                                value={zoneRankUpper} 
                                onChange={e => setZoneRankUpper(e.target.value)} 
                                style={{ height:'34px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', color: '#fff', fontSize: '0.78rem', padding: '0 0.5rem', outline: 'none', width: '100%', boxSizing: 'border-box' }} 
                              />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1 }}>
                              <label style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' }} title="Target Lower Buffer (worse ranks). Ranks worse than Student Rank + Buffer are Safe.">
                                Target Lower (Rank)
                              </label>
                              <input 
                                className="hide-spinners" 
                                type="number" 
                                value={zoneRankLower} 
                                onChange={e => setZoneRankLower(e.target.value)} 
                                style={{ height:'34px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', color: '#fff', fontSize: '0.78rem', padding: '0 0.5rem', outline: 'none', width: '100%', boxSizing: 'border-box' }} 
                              />
                            </div>
                          </div>
                        )}

                        {/* Percentile buffers */}
                        {zoneBasis === 'percentile' && (
                          <div style={{ display: 'flex', gap: '0.5rem', flex: '1 1 200px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1 }}>
                              <label style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' }} title="Target Upper Buffer (higher percentiles). Percentiles higher than Student Percentile + Buffer are Dream.">
                                Target Upper (%)
                              </label>
                              <input 
                                className="hide-spinners" 
                                type="number" 
                                step="0.1"
                                value={zonePercUpper} 
                                onChange={e => setZonePercUpper(e.target.value)} 
                                style={{ height:'34px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', color: '#fff', fontSize: '0.78rem', padding: '0 0.5rem', outline: 'none', width: '100%', boxSizing: 'border-box' }} 
                              />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1.2 }}>
                              <label style={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' }} title="Target Lower Buffer (lower percentiles). Percentiles lower than Student Percentile - Buffer are Safe.">
                                Target Lower (%)
                              </label>
                              <div style={{ display: 'flex', gap: '4px', width: '100%', boxSizing: 'border-box' }}>
                                <select 
                                  className="pcw-select-input" 
                                  value={zonePercLowerMode} 
                                  onChange={e => setZonePercLowerMode(e.target.value)} 
                                  style={{background:'rgba(0,0,0,0.3)', padding:'0 0.3rem', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.08)', color:'#fff', height:'34px', fontSize:'0.75rem', flex: 1}}
                                >
                                  <option value="auto">Auto</option>
                                  <option value="custom">Custom</option>
                                </select>
                                {zonePercLowerMode === 'custom' && (
                                  <input 
                                    className="hide-spinners" 
                                    type="number" 
                                    step="0.1"
                                    value={zonePercLowerCustom} 
                                    onChange={e => setZonePercLowerCustom(e.target.value)} 
                                    style={{ height:'34px', width: '55px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', color: '#fff', fontSize: '0.78rem', padding: '0 0.3rem', outline: 'none', boxSizing: 'border-box' }} 
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      </div>

                      <div className="pcw-filter-actions">
                      {examTab === "CET" && (
                        <div className="pcw-switch-wrap" onClick={() => setIs2026R1(!is2026R1)} style={{marginRight: '1rem', background: is2026R1 ? 'rgba(14, 165, 233, 0.15)' : 'transparent', border: is2026R1 ? '1px solid rgba(14, 165, 233, 0.4)' : '1px solid transparent', padding: '2px 8px', borderRadius: '8px', transition: 'all 0.2s ease'}}>
                          <div className={`pcw-switch ${is2026R1 ? 'on' : ''}`}></div>
                          <span style={{color: is2026R1 ? '#38bdf8' : '#94a3b8', fontWeight: is2026R1 ? 'bold' : 'normal'}}>Switch to 2026 R1</span>
                        </div>
                      )}
                      {examTab === "CET" && (
                        <div className="pcw-switch-wrap" onClick={() => setShowRankPredictor(!showRankPredictor)}>
                          <div className={`pcw-switch ${showRankPredictor ? 'on' : ''}`}></div>
                          <span style={{color: showRankPredictor ? '#0ea5e9' : '#94a3b8'}}>Rank Predictor</span>
                        </div>
                      )}
                      <div className="pcw-switch-wrap" onClick={() => setEnablePriority(!enablePriority)} style={{marginLeft: examTab === "CET" ? '1rem' : '0'}}>
                        <div className={`pcw-switch ${enablePriority ? 'on' : ''}`}></div>
                        <span style={{color: enablePriority ? '#0ea5e9' : '#94a3b8'}}>Priority</span>
                      </div>
                      <div className="pcw-switch-wrap" onClick={() => setPrintRank(!printRank)} style={{marginLeft:'1rem'}}>
                        <div className={`pcw-switch ${printRank ? 'on' : ''}`}></div>
                        <span style={{color: printRank ? '#0ea5e9' : '#94a3b8'}}>Print Rank</span>
                      </div>
                      <div className="pcw-switch-wrap" onClick={() => setPrintBranchRank(!printBranchRank)} style={{marginLeft:'1rem'}}>
                        <div className={`pcw-switch ${printBranchRank ? 'on' : ''}`}></div>
                        <span style={{color: printBranchRank ? '#0ea5e9' : '#94a3b8'}}>Print Branch Rank</span>
                      </div>
                      {results.length > 0 && (
                        <button className="pcw-topbtn" onClick={() => setShowFilters(false)} style={{marginRight:'0.5rem', background: 'rgba(255, 255, 255, 0.05)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.08)', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600', display: 'inline-flex', alignItems: 'center', height: '36px'}}>
                           <i className="fas fa-eye-slash" style={{marginRight:5}}></i>Hide Filters
                        </button>
                      )}
                      <button className="pcw-generate-btn" onClick={handleGenerate} disabled={generating || (!percentile && !rank)}>
                        {generating ? <><i className="fas fa-spinner fa-spin" style={{marginRight:5}}></i>Generating...</> : <><i className="fas fa-rocket" style={{marginRight:5}}></i>Generate</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* Results table — Task 4: full width, drag-to-reorder locked rows */}
                {results.length > 0 && (
                  <div className="pcw-results">
                    <div className="pcw-results-header">
                      <div>
                        <h4><i className="fas fa-list" style={{marginRight:5,color:'#0ea5e9'}}></i>Results</h4>
                        <span className="pcw-results-count">{displayResults.length} of {results.length} shown</span>
                      </div>
                      <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
                        {displayResults.length > 0 && (
                          <button className="pcw-bulk-delete-btn" onClick={handleDeleteBelow}>
                            <i className="fas fa-trash-alt" style={{marginRight:5}}></i>Delete Options Below
                          </button>
                        )}
                        <input className="pcw-result-search" placeholder="Search institute or branch..." value={resultSearch} onChange={e=>setResultSearch(e.target.value)} />
                        
                        {/* Search & Add College */}
                        {!isActiveVersionFinalized() && (
                          <div className="pcw-manual-add-wrap">
                            {showManualAdd ? (
                              <div style={{display:'flex',gap:'0.25rem',position:'relative'}}>
                                <input 
                                  className="pcw-result-search" 
                                  placeholder="Type to search college..." 
                                  value={manualQuery}
                                  onChange={handleManualSearchChange}
                                  autoFocus 
                                  style={{borderColor:'#10b981',width:'240px'}}
                                />
                                <button className="pcw-modal-cancel" onClick={() => { setShowManualAdd(false); setManualQuery(""); setManualResults([]); }} style={{padding:'0 8px',fontSize:'0.75rem'}}>
                                  Cancel
                                </button>
                                {manualResults.length > 0 && (
                                  <div className="pcw-manual-add-dropdown">
                                    {manualResults.map((college, idx) => {
                                      const inst = college["INSTITUTE"] || college["Institute Name"] || "";
                                      const branch = college["BRANCH NAME"] || college["Course Name"] || "";
                                      const city = (college["CITY"] || "").replace(/MUNMBAI/gi, "MUMBAI");
                                      const category = college["CATEGORY"] || "";
                                      const rank = college["Rank"] || college["AI Merit"] || "-";
                                      const percent = college["Percentile"] || college["Percen Tile"] || "-";
                                      const code = college["Branch Code"] || college["Choice Code"] || "";
                                      return (
                                        <div key={idx} className="pcw-manual-add-option" onClick={() => handleSelectCollegeToAdd(college)}>
                                          <strong>{inst}</strong>
                                          <span>{branch} ({code})</span>
                                          <span className="pcw-manual-add-meta">
                                            {city} {category ? `| ${category}` : ''} | Rank: {rank} ({percent}%)
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <button className="pcw-add-college-btn" onClick={() => setShowManualAdd(true)}>
                                <i className="fas fa-plus" style={{marginRight:5}}></i>Add College/Branch
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="pcw-rtable-wrap">
                      <div className="pcw-rtable">
                        {/* Header */}
                        <div className={`pcw-rtable-header ${examTab==='CET'?'cet':'jee'}`}>
                          <div className="pcw-header-cell" style={{width:42,whiteSpace:'nowrap'}}>#</div>
                          <div className="pcw-header-cell">Institute</div>
                          <div className="pcw-header-cell">{examTab==='CET'?'Branch':'Course'}</div>
                          <div className="pcw-header-cell sortable" onClick={()=>{setSortField('Branch Rank');setSortDir(d=>d==='asc'?'desc':'asc');}}>
                            Branch Rank <i className="fas fa-sort" style={{fontSize:'0.55rem',marginLeft:4}}></i>
                          </div>
                          {examTab==='CET' && <div className="pcw-header-cell">Category</div>}
                          <div className="pcw-header-cell">City</div>
                          <div className="pcw-header-cell sortable" onClick={()=>{const f=examTab==='CET'?'Rank':'AI Merit';setSortField(f);setSortDir(d=>d==='asc'?'desc':'asc');}}>
                            {examTab==='CET'?'Rank':'AI Merit'} <i className="fas fa-sort" style={{fontSize:'0.55rem',marginLeft:4}}></i>
                          </div>
                          <div className="pcw-header-cell sortable" onClick={()=>{const f=examTab==='CET'?'Percentile':'Percen Tile';setSortField(f);setSortDir(d=>d==='asc'?'desc':'asc');}}>
                            Percentile <i className="fas fa-sort" style={{fontSize:'0.55rem',marginLeft:4}}></i>
                          </div>
                          <div className="pcw-header-cell" style={{width:70}}></div>
                        </div>

                        {/* Body */}
                        <div className="pcw-rtable-body">
                          {displayResults.slice(0, 300).map((r, i) => {
                            const rowKey = r._key || i;
                            const isSelectedMove = selectedMoveIdx === i;
                            let zone;
                            try { zone = getAdmissionCategory(r); }
                            catch { zone = { label: "Target", colorClass: "target", borderLeft: "3px solid #fbbf24" }; }
                            return (
                              <div 
                                key={rowKey} 
                                className={`pcw-row-interactive ${isSelectedMove ? 'selected-move' : ''} ${zone.colorClass} ${examTab==='CET'?'cet':'jee'}`}
                                onClick={() => handleRowClick(i)}
                              >
                                <div className="pcw-cell" style={{width:42,color:isSelectedMove?'#38bdf8':'#94a3b8',fontWeight:700,justifyContent:'center',whiteSpace:'nowrap'}}>
                                  {i + 1}
                                </div>
                                <div className="pcw-cell" style={{fontWeight:600,color:'#f1f5f9'}} title={r["INSTITUTE"]||r["Institute Name"]||""}>
                                  {r["INSTITUTE"]||r["Institute Name"]||""}
                                </div>
                                <div className="pcw-cell" title={r["BRANCH NAME"]||r["Course Name"]||""}>
                                  {r["BRANCH NAME"]||r["Course Name"]||""}
                                </div>
                                <div className="pcw-cell" style={{fontFamily:'monospace',fontSize:'0.74rem'}}>
                                  {r["Branch Rank"] || "-"}
                                </div>
                                {examTab==='CET' && (
                                  <div className="pcw-cell">
                                    <span style={{fontSize:'0.68rem',padding:'1px 5px',borderRadius:4,background:'rgba(14,165,233,0.1)',color:'#38bdf8'}}>
                                      {r["CATEGORY"]||""}
                                    </span>
                                  </div>
                                )}
                                <div className="pcw-cell">{(r["CITY"]||"").replace(/MUNMBAI/gi, "MUMBAI")}</div>
                                <div className="pcw-cell" style={{fontFamily:'monospace',fontSize:'0.74rem'}}>{examTab==='CET'?(r["Rank"]||""):(r["AI Merit"]||"")}</div>
                                <div className="pcw-cell" style={{fontFamily:'monospace',fontSize:'0.74rem',display:'flex',alignItems:'center'}}>
                                  {r["Percentile"]||r["Percen Tile"]||""}
                                  <span 
                                    className={`pcw-zone-badge ${zone.colorClass}`}
                                    onClick={(e) => handleToggleZone(e, i)}
                                    style={{cursor: isActiveVersionFinalized() ? 'default' : 'pointer'}}
                                    title={isActiveVersionFinalized() ? "" : "Click to manually change category"}
                                  >
                                    {zone.label}
                                  </span>
                                </div>
                                <div className="pcw-cell" style={{width:70,justifyContent:'center',gap:'4px'}} onClick={(e) => e.stopPropagation()}>
                                  {!isActiveVersionFinalized() && (
                                    <>
                                      <button 
                                        className="pcw-move-btn" 
                                        disabled={i === 0} 
                                        onClick={(e) => moveItemInDisplay(i, "up", e)} 
                                        title="Move Up"
                                      >
                                        <i className="fas fa-chevron-up"></i>
                                      </button>
                                      <button 
                                        className="pcw-move-btn" 
                                        disabled={i === displayResults.length - 1} 
                                        onClick={(e) => moveItemInDisplay(i, "down", e)} 
                                        title="Move Down"
                                      >
                                        <i className="fas fa-chevron-down"></i>
                                      </button>
                                    </>
                                  )}
                                  <button className="pcw-remove-btn" onClick={() => removeFromResults(r)} title="Remove from list">
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </React.Fragment>
      )}
    </div>
      </div>

      {/* Student Modal */}
      {showStudentModal && (
        <div className="pcw-modal-overlay" onClick={() => setShowStudentModal(false)}>
          <div className="pcw-modal" onClick={e => e.stopPropagation()}>
            <h3><i className="fas fa-user-plus" style={{marginRight:7,color:'#0ea5e9'}}></i>Create Student</h3>
            <label>Registration Number</label>
            <input value={newStudentForm.registration_number} onChange={e => setNewStudentForm({...newStudentForm, registration_number: e.target.value})} placeholder="REG2025001" />
            <label>Student Name</label>
            <input value={newStudentForm.name} onChange={e => setNewStudentForm({...newStudentForm, name: e.target.value})} placeholder="Full name" />
            <label>Mobile Number</label>
            <input type="tel" value={newStudentForm.mobile} onChange={e => setNewStudentForm({...newStudentForm, mobile: e.target.value})} placeholder="10-digit mobile" />
            <label>Password</label>
            <input type="password" value={newStudentForm.password} onChange={e => setNewStudentForm({...newStudentForm, password: e.target.value})} placeholder="••••••••" />
            
            {user.role === 'ADMIN' && (
              <>
                <label>Branch Assignment</label>
                <select 
                  value={newStudentForm.branch} 
                  onChange={e => setNewStudentForm({...newStudentForm, branch: e.target.value})}
                  className="pcw-select-input"
                  style={{ marginBottom: '1rem' }}
                >
                  <option value="AME Pune">AME Pune</option>
                  <option value="AME Latur">AME Latur</option>
                </select>
              </>
            )}
            
            <label>Student Category</label>
            <select 
              value={newStudentForm.category} 
              onChange={e => setNewStudentForm({...newStudentForm, category: e.target.value})}
              className="pcw-select-input"
              style={{ marginBottom: '1rem' }}
            >
              <option value="OPEN">OPEN / General</option>
              <option value="OBC">OBC (Other Backward Class)</option>
              <option value="SC">SC (Scheduled Caste)</option>
              <option value="ST">ST (Scheduled Tribe)</option>
              <option value="VJ">VJ / DT (Vimukta Jati)</option>
              <option value="NT1">NT-B (NT1)</option>
              <option value="NT2">NT-C (NT2)</option>
              <option value="NT3">NT-D (NT3)</option>
              <option value="SBC">SBC (Special Backward Class)</option>
              <option value="EWS">EWS (Economically Weaker Section)</option>
              <option value="SEBC">SEBC (Socially & Educationally Backward Class)</option>
            </select>
            <label>Special Reservation <span style={{fontSize:'0.65rem',color:'#94a3b8',textTransform:'none'}}>(Optional - e.g. PH, Defense, Orphan)</span></label>
            <input value={newStudentForm.special_reservation} onChange={e => setNewStudentForm({...newStudentForm, special_reservation: e.target.value})} placeholder="e.g. PH, Defense" style={{marginBottom:'1.25rem'}} />
            <div className="pcw-modal-btns">
              <button className="pcw-modal-cancel" onClick={() => setShowStudentModal(false)}>Cancel</button>
              <button className="pcw-modal-ok" onClick={handleCreateStudent}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditStudentModal && (
        <div className="pcw-modal-overlay" onClick={() => setShowEditStudentModal(false)}>
          <div className="pcw-modal" onClick={e => e.stopPropagation()}>
            <h3><i className="fas fa-user-edit" style={{marginRight:7,color:'#0ea5e9'}}></i>Edit Student</h3>
            <label>Registration Number</label>
            <input value={editStudentForm.registration_number} onChange={e => setEditStudentForm({...editStudentForm, registration_number: e.target.value})} placeholder="REG2025001" />
            <label>Student Name</label>
            <input value={editStudentForm.name} onChange={e => setEditStudentForm({...editStudentForm, name: e.target.value})} placeholder="Full name" />
            <label>Mobile Number</label>
            <input type="tel" value={editStudentForm.mobile} onChange={e => setEditStudentForm({...editStudentForm, mobile: e.target.value})} placeholder="10-digit mobile" />
            <label>Password <span style={{fontSize:'0.65rem',color:'#94a3b8',textTransform:'none'}}>(Leave blank to keep current)</span></label>
            <input type="password" value={editStudentForm.password} onChange={e => setEditStudentForm({...editStudentForm, password: e.target.value})} placeholder="••••••••" />
            
            {user.role === 'ADMIN' && (
              <>
                <label>Branch Assignment</label>
                <select 
                  value={editStudentForm.branch} 
                  onChange={e => setEditStudentForm({...editStudentForm, branch: e.target.value})}
                  className="pcw-select-input"
                  style={{ marginBottom: '1rem' }}
                >
                  <option value="AME Pune">AME Pune</option>
                  <option value="AME Latur">AME Latur</option>
                </select>
              </>
            )}

            <label>Student Category</label>
            <select 
              value={editStudentForm.category} 
              onChange={e => setEditStudentForm({...editStudentForm, category: e.target.value})}
              className="pcw-select-input"
              style={{ marginBottom: '1rem' }}
            >
              <option value="OPEN">OPEN / General</option>
              <option value="OBC">OBC (Other Backward Class)</option>
              <option value="SC">SC (Scheduled Caste)</option>
              <option value="ST">ST (Scheduled Tribe)</option>
              <option value="VJ">VJ / DT (Vimukta Jati)</option>
              <option value="NT1">NT-B (NT1)</option>
              <option value="NT2">NT-C (NT2)</option>
              <option value="NT3">NT-D (NT3)</option>
              <option value="SBC">SBC (Special Backward Class)</option>
              <option value="EWS">EWS (Economically Weaker Section)</option>
              <option value="SEBC">SEBC (Socially & Educationally Backward Class)</option>
            </select>
            <label>Special Reservation <span style={{fontSize:'0.65rem',color:'#94a3b8',textTransform:'none'}}>(Optional - e.g. PH, Defense, Orphan)</span></label>
            <input value={editStudentForm.special_reservation} onChange={e => setEditStudentForm({...editStudentForm, special_reservation: e.target.value})} placeholder="e.g. PH, Defense" style={{marginBottom:'1.25rem'}} />
            <div className="pcw-modal-btns">
              <button className="pcw-modal-cancel" onClick={() => setShowEditStudentModal(false)}>Cancel</button>
              <button className="pcw-modal-ok" onClick={handleEditStudentSubmit}>Update</button>
            </div>
          </div>
        </div>
      )}

      {/* Save Draft Version Modal */}
      {showSaveVersionModal && (
        <div className="pcw-modal-overlay" onClick={() => setShowSaveVersionModal(false)}>
          <div className="pcw-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <h3><i className="fas fa-save" style={{marginRight:7,color:'#0ea5e9'}}></i>Save Draft Version</h3>
            <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '1.25rem', lineHeight: '1.4' }}>
              Would you like to overwrite/update the current draft version (<strong>{studentVersions.find(v => v.id === activeVersionId)?.version_label}</strong>) or save it as a brand new version?
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, marginBottom: '0.35rem' }}>New Version Name (Optional)</label>
              <input 
                value={newVersionName} 
                onChange={e => setNewVersionName(e.target.value)} 
                placeholder="e.g. V2 Revised" 
                style={{ width: '100%', padding: '0.45rem 0.7rem', borderRadius: '8px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div className="pcw-modal-btns" style={{ flexDirection: 'column', gap: '0.5rem', alignItems: 'stretch' }}>
              <button className="pcw-modal-ok" onClick={updateExistingVersion} disabled={saving} style={{ padding: '0.6rem' }}>
                <i className="fas fa-edit" style={{ marginRight: 5 }}></i> Update Current Draft
              </button>
              <button className="pcw-modal-ok" onClick={() => saveNewVersion(newVersionName)} disabled={saving} style={{ background: '#10b981', padding: '0.6rem' }}>
                <i className="fas fa-plus-circle" style={{ marginRight: 5 }}></i> Save as New Version
              </button>
              <button className="pcw-modal-cancel" onClick={() => setShowSaveVersionModal(false)} style={{ padding: '0.6rem' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Version Modal */}
      {showDuplicateModal && (
        <div className="pcw-modal-overlay" onClick={() => setShowDuplicateModal(false)}>
          <div className="pcw-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h3><i className="fas fa-copy" style={{marginRight:7,color:'#10b981'}}></i>Duplicate Locked Version</h3>
            <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '1rem', lineHeight: '1.4' }}>
              This list is finalized and locked. Create an editable copy of this list under a new version name.
            </p>
            <label>New Version Name</label>
            <input 
              value={duplicateVersionName} 
              onChange={e => setDuplicateVersionName(e.target.value)} 
              placeholder="e.g. V2 (Copy)" 
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleDuplicateVersion(); }}
              style={{ width: '100%', padding: '0.45rem 0.7rem', borderRadius: '8px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', outline: 'none', boxSizing: 'border-box', marginBottom: '1rem' }}
            />
            <div className="pcw-modal-btns">
              <button className="pcw-modal-cancel" onClick={() => setShowDuplicateModal(false)}>Cancel</button>
              <button className="pcw-modal-ok" onClick={handleDuplicateVersion} style={{background:'#10b981'}}>Duplicate</button>
            </div>
          </div>
        </div>
      )}

      {/* Position Selection Modal */}
      {showPositionModal && selectedCollegeToAdd && (
        <div className="pcw-modal-overlay" onClick={() => { setShowPositionModal(false); setSelectedCollegeToAdd(null); }}>
          <div className="pcw-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h3><i className="fas fa-arrow-down-short-wide" style={{marginRight:7,color:'#10b981'}}></i>Select Insertion Position</h3>
            <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '1rem', lineHeight: '1.4' }}>
              Select where you want to insert <strong>{selectedCollegeToAdd["INSTITUTE"] || selectedCollegeToAdd["Institute Name"]}</strong> - <em>{selectedCollegeToAdd["BRANCH NAME"] || selectedCollegeToAdd["Course Name"]}</em>.
            </p>
            <label>Insert at Rank/Position (1 to {results.length + 1})</label>
            <input 
              type="number" 
              min="1" 
              max={results.length + 1}
              value={insertPosition} 
              onChange={e => setInsertPosition(e.target.value)}
              placeholder={`Default: ${results.length + 1}`}
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleInsertCollege(); }}
            />
            <div className="pcw-modal-btns">
              <button className="pcw-modal-cancel" onClick={() => { setShowPositionModal(false); setSelectedCollegeToAdd(null); }}>Cancel</button>
              <button className="pcw-modal-ok" onClick={handleInsertCollege} style={{background:'#10b981'}}>Insert</button>
            </div>
          </div>
        </div>
      )}
      {/* Scroll-to-Top Floating Button */}
      {showScrollTop && (
        <button
          className="pcw-scroll-top-btn"
          onClick={() => mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          title="Scroll to top (Alt + T)"
        >
          <i className="fas fa-arrow-up"></i>
        </button>
      )}

    </>
  );
}



